import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Stack, Menu, MenuItem, Grid, Badge, useMediaQuery, Alert, AlertTitle } from '@mui/material';
import { AddOutlined } from '@mui/icons-material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import HandymanIcon from '@mui/icons-material/Handyman';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import DisabledByDefaultOutlinedIcon from '@mui/icons-material/DisabledByDefaultOutlined';
import { Button, ButtonColorProps, ButtonSizeProps, ButtonVariantProps } from '../../../../../components/atoms/Button';
import { SelectMenu } from '../../../../../components/atoms/SelectMenu';
import { Loader } from '../../../../../components/atoms/Loader';
import { ServiceNodeAllocation, ServiceNodeAllocationType } from '../../../../../@types/allocation.type';
import { checkForSelectedRules, getServiceAttributesForServiceCreationForm, statusOptionItems, checkSetParameterNoUIFormAvailability, checkGetGeoLocationFormAvailability, generateServiceURL, checkModifyStructureToggleActiveFormAvailability, processBulkCreateData, getCheckedInData } from '../../../../../utils/services';
import { ServiceAllocationSelector } from '../../../../../components/molecules/ServiceAllocationSelector';
import { PopupDialog } from '../../../../../components/molecules/PopupDialog';
import { getNewTagsMapped, getSelectedExistingTagsMapped } from '../../../../../components/organisms/Tags';
import { MerchantDamagePartSerialMenuData, PartConditionOptions } from '../../../../../@types/part.type';
import { PendingInteractionFormType } from '../../../../../@types/pendingInteraction.type';
import { GRID_SPACING } from '../../../../../configs/ui-constants';
import { PartActionInstallCard } from '../PartActionInstallCard';
import { FormInterceptor } from '../FormInterceptor';
import { PartActionReplaceCard } from '../PartActionReplaceCard';
import { TaskTimeCard } from '../TaskTimeCard';
import { TaskQuantityCard } from '../TaskQuantityCard';
import { TaskQuoteValueCard } from '../TaskQuoteValueCard';
import { TaskServiceSpecialCard } from '../TaskServiceSpecialCard';
import { PartActionCleanedCard } from '../PartActionCleanedCard';
import { PartActionRemoveCard } from '../PartActionRemoveCard';
import { PartActionDoaCard } from '../PartActionDoaCard/PartActionDoaCard';
import { useGetPartActionByServiceId, useGetPartActionTypes } from '../../../../../queries/partAction-query';
import { getExceptionPartImageFileKey, getExceptionPartImageFileName, getPartActionTypesDropdownData, getPendingPartIdsToCheck, isPartActionsSubmitDisabled, mapPartActions, preProcessPartActions, validatePartActionHelperTexts } from '../../../../../utils/part-action';
import { FormInterceptorContext, FormInterceptorContextProps } from '../../../../auth';
import { useModifyStructuresToggleActive, useSubmitFormsDataWithOriginalPayload } from '../../../../../queries/interactions-query';
import { formatDate, formatDateSimple, getCurrentTime, getCurrentTimestamp, getDateFromTimestamp, getJSTimestamp, getTimestamp, isDropDownEmpty, isEmptyString } from '../../../../../utils/common';
import {
  useAddService,
  useUpdateParentService,
  useUpdateReportedInformation,
  useUpdateService,
  useUpdateServiceAllocation
} from '../../../../../queries/service-query';
import { useAddTags } from '../../../../../queries/tags-query';
import { useGetTaskByServiceId, useGetTaskTypes } from '../../../../../queries/task-query';
import { useCreateStructure, useGetSuggestedNodesFromAllocationEngine } from '../../../../../queries/structure-query';
import { useGetPendingInteractionsByServiceId } from '../../../../../queries/pendingInteraction-query';
import {
  ActionPermissions,
  ActionTriggerFormActionType,
  CustomPermission,
  FormContainerItem,
  FormFieldItem,
  FormFieldType,
  FormObject,
  LocationNodeData,
  MerchantDamageObjectParcer,
  ModifyStructureFormModifyStructureType,
  ParameterDataFormParameterType,
  PartConditionName,
  PartTypeActionLiveSearchItem,
  ServiceNodeAllocationData, 
  ServiceStatusLabel, 
  Technician, 
  ScreenSize,
  ServiceStatusCode,
  AllocatedService,
  CheckInObject,
  CheckOutObject,
  InterceptedFormsObject
} from '../../../../../@types';
import { TaskCard, TaskType, TaskTypeCategory, TaskTypes } from '../../../../../@types/task.type';
import { EntityType, ServiceAttachmentUploadFeature, ServiceCancelOrReOpen } from '../../../../../configs/enums';
import { SAMPLE_TASK_CARD } from '../../../../../constants/task';
import { HttpMethod } from '../../../../../api/http-client';
import { ServiceActionBarProps } from './ServiceActionBar.props';
import {
  ContentPaper,
  IconContainer,
  LightbulbIcon,
  PrimaryAllocationIcon,
  AccountIcon,
  ForwardIcon,
  LocationText,
  LocationSubText,
  AccountText,
  NewButton,
  NotesText,
  SubdirectoryArrowIcon,
  MenuIcon,
  MenuButton,
  SecondaryAllocationIcon,
  ActionBarStack,
  ClickableStack,
  AddButton,
  StyledSelectMenu,
  FullWidthBadge,
  FullWidthMenuItem,
  StyledActionBarStack,
  SelectStatusContainer,
  ZapSquareIcon,
  FullWidthBadgeInteraction,
  CancelButtonWrapStack,
  StatusSaveButtonWrap,
  StatusbarHeadingText,
  ControlPointRoundedIcon,
  StatusbarItemGrid,
  StatusbarItemHeadingGrid,
  PartActionContainer,
  PartActionCardWrapper
} from './ServiceActionBar.styles';
import { PartActionType, CustomisePartActionCard, PartActionCardHelperText, PartCreationOptionData, Action, ExceptionalPartData, ExceptionalPartMetaData, PendingShipmentsDialog, PendingShipmentsDataType, PartActionCard } from '../../../../../@types/partAction.type';
import { SAMPLE_PART_ACTION_CARD_HELPER_TEXT, VALID_PART_ACTION_TYPE_CODES } from '../../../../../constants/partAction';
import { uploadFiles } from '../../../../../api/uploadedFiles';
import { Folders } from '../../../../../@types/uploaded-files.type';
import { useCreateExceptionalPart, useUpdatePartAdditionalData } from '../../../../../queries/part-query';
import { isUserHasPartActionPermissions, isUserHasPermission } from '../../../../../configs/permissions';
import { useGetUserSystemPermissions } from '../../../../../queries/user-query';
import { checkServiceCancelOrFutileAvailability } from '../../../../../utils/service-status';
import { useGetServiceCancelOrReopenStatuses } from '../../../../../queries/service-status-query';
import { blockInteractionAvailable, checkFieldIsError, checkIsByPassed, getBlockInteractionMessage, getFormIsRepeatableStatus, getSortedPendingInteractions, hasFormFieldValueError } from '../../../../../utils/interactions';
import { useCreateShipmentIndividualReceive, useGetShipmentDetailsPartIncludes } from '../../../../../queries/shipment-query';
import { ShipmentManifestSummery } from '../../../../../@types/shipment.type';
import { PartActionShipmentReceive } from '../../../../../components/molecules/PartActionShipmentReceive';
import { useGetUploadedImages } from '../../../../../queries/uploadedfiles-query';
import { ImagePopupSlide } from '../../../../../components/atoms/ImagePopup';
import { PartActionPartCreationDialog, PartImageData } from '../PartActionPartCreationDialog';
import { Snackbar } from '../../../../../components/atoms/Snackbar';
import { useGetServiceLogsByServiceId } from '../../../../../queries/service-log-query';
import { Typography, TypographyVariantProps } from '../../../../../components/atoms/Typography';
import { COLORS } from '../../../../../configs/colors';
import { CancelReOpenStatusKey } from '../../../../../configs/service-status';
import { PartActionRepairCard } from '../PartActionRepairCard';
import { PartActionTestedCard } from '../PartActionTestedCard';
import { PartActionAdjustCard } from '../PartActionAdjustCard';
import { ServiceNewCheckIn } from '../../../../../components/organisms/ServiceNewCheckIn';
import { useGetCurrentUserAllocatedServices, useGetServiceCheckIn } from '../../../../../queries/service-checkin-checkout-query';

export const ServiceActionBar: React.FC<ServiceActionBarProps> = ({
  service,
  currentStatusLabel,
  statusOptions,
  statusValue,
  noteText,
  technicians,
  isHelpdeskUser,
  primaryAllocation,
  secondaryAllocation,
  partActionCardDataset,
  taskCardDataset,
  onChangeStatusValue,
  onNoteText,
  onAllocationChange,
  onSubmit,
  onChangePartActionCard,
  onExecPartActions,
  onChangeTaskCard,
  onSaveTaskCard,
  onExecTaskCard,
  onCloseAndChangePopupTaskCard,
  handlePartActionCardClose
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [isStatusChange, setIsStatusChange] = useState(false);
  const [isAllocationChange, setIsAllocationChange] = useState(false);
  const [isTask, setIsTask] = useState(false);
  const [isPartActionPopup, setIsPartActionPopup] = useState(false);
  const [isTaskPopup, setIsTaskPopup] = useState(false);
  const [merchantDamageObjectParcer, setMerchantDamageObjectParcer] = useState<MerchantDamageObjectParcer>();
  const [partActionCardType, setPartActionCardType] = useState<PartActionType>(PartActionType.INSTALL);
  const [taskType, setTaskType] = useState<string>(TaskType.LABOUR_TIME);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [anchorElTechnicians, setAnchorElTechnicians] = React.useState<null | HTMLElement>(null);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [selectedNodeAllocation, setSelectedNodeAllocation] = useState<ServiceNodeAllocation>();
  const [isPendingInteractions, setIsPendingInteractions] = useState(false);
  const [pendingInteractionFormData, setPendingInteractionFormData] = useState<PendingInteractionFormType[]>();
  const [suggestedNodesFromAllocation, setSuggestedNodesFromAllocation] = useState<LocationNodeData[]>([]);
  const [permissions, setPermissions] = useState<CustomPermission[]>();
  const [isCancelOrReopen, setIsCancelOrReopen] = useState<string>('');
  const [submitInteractions, setSubmitInteractions] = useState(false);
  const [isPartAction, setIsPartAction] = useState(false);
  const [helperText, setHelperText] = useState<PartActionCardHelperText[]>([SAMPLE_PART_ACTION_CARD_HELPER_TEXT]);
  const [newServiceId, setNewServiceId] = useState<number>(0);
  const [originalInterceptedForms, setOriginalInterceptedForms] = useState<FormObject[]>([]);
  const [repeatableFormData, setRepeatableFormData] = useState({ templateId: 0, templateTitle: '' });
  const [openShipmentReceivePopup, setOpenShipmentReceivePopup] = useState(false);
  const [shipmentManifestSummery, setShipmentManifestSummery] = useState<ShipmentManifestSummery>();
  const [selectedPartActionCard, setSelectedPartActionCard] = useState<CustomisePartActionCard>();
  const [openPartCreationPopup, setOpenPartCreationPopup] = useState(false);
  const [partTypeValue, setPartTypeSearchValue] = useState<PartTypeActionLiveSearchItem>();
  const [partTypeImages, setPartTypeImages] = useState<Array<ImagePopupSlide>>([]);
  const [selectedPartActionData, setSelectedPartActionData] = useState<PartCreationOptionData>();
  const [partImages, setPartImages] = useState<PartImageData>();
  const [showWarningToast, setShowWarningToast] = useState(false);
  const [validate, setValidate] = useState(false);
  const [taskTypes, setTaskTypes] = useState<TaskTypes[]>([]);
  const [partActionsExecuting, setPartActionsExecuting] = useState(false);
  const [allocatedServices, setAllocatedServices] = useState<AllocatedService[]>([]);
  const [notesText, setNotesText] = useState<string>('');
  const [travelStart, setTravelStart] = useState<string>('');
  const [onSiteTime, setOnSiteTime] = useState<string>('');
  const [checkedInService, setCheckedInService] = useState<AllocatedService[]>([]);
  const [notCheckedInService, setNotCheckedInService] = useState<AllocatedService[]>([]);
  const [pendingPartCardId, setPendingPartCardId] = useState<number | null>(null);
  const [pendingPartsToCreate, setPendingPartsToCreate] = useState<Record<number, Array<ExceptionalPartData>>>({});
  const [pendingShipmentDialogData, setPendingShipmentDialogData] = useState<PendingShipmentsDialog | null>(null);
  const pendingShipmentsDataRef = useRef<PendingShipmentsDataType>({ accepted: [], canceled: [] });

  const { interceptedFormsObject, setInterceptedFormsObject, setHasInterceptedForms } = useContext<FormInterceptorContextProps>(FormInterceptorContext);
  const open = Boolean(anchorEl);
  const openTechniciansMenu = Boolean(anchorElTechnicians);
  const isMobileView = useMediaQuery(ScreenSize.MOBILE);

  const getPendingInteractionsByServiceId = useGetPendingInteractionsByServiceId(service.id);
  const addServiceQuery = useAddService();
  const updateParentServiceQuery = useUpdateParentService(service.id);
  const submitFormsDataWithOriginalPayloadQuery = useSubmitFormsDataWithOriginalPayload(service.id);
  const createStructure = useCreateStructure();
  const updateServiceQuery = useUpdateService(service.id);
  const updateServiceAllocationQuery = useUpdateServiceAllocation(service.id);
  const updateReportedInformation = useUpdateReportedInformation();
  const updateTagsQuery = useAddTags();
  const updatePartAdditionalData = useUpdatePartAdditionalData(merchantDamageObjectParcer?.serialData?.partId || 0);
  const getSuggestedNodesFromAllocationEngine = useGetSuggestedNodesFromAllocationEngine(service.serviceTypeCode, service.actionTypeCode, service.processTypeCode, Number(service.brandId !== null ? service.brandId : service.parentId), service.mainLocationNodeId, service.id);
  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const getServiceCancelOrReopenStatuses = useGetServiceCancelOrReopenStatuses(isCancelOrReopen, service.serviceStatusCode, service.serviceTypeCode, service.id);
  const getShipmentDetailsPartIncludes = useGetShipmentDetailsPartIncludes();
  const createShipmentIndividualReceive = useCreateShipmentIndividualReceive(shipmentManifestSummery?.shipmentId || NaN);
  const partTypeImagesQuery = useGetUploadedImages(partTypeValue?.partTypeId || 0, Folders.PartTypesImages);
  const createPartQuery = useCreateExceptionalPart();
  const getPartActionsByServiceId = useGetPartActionByServiceId(service.id);
  const getTasksByServiceIdQuery = useGetTaskByServiceId(service.id);
  const getServiceLogsByServiceIdQuery = useGetServiceLogsByServiceId(service.id);
  const getPartActionTypes = useGetPartActionTypes();
  const getTaskTypes = useGetTaskTypes(service.id);
  const modifyStructuresToggleActive = useModifyStructuresToggleActive();
  const getServiceCheckInQuery = useGetServiceCheckIn(service.id);
  const getCurrentUserAllocatedServices = useGetCurrentUserAllocatedServices(service.mainLocationNodeId ?? 0, service.id);

  const queryClient = useQueryClient();

  useEffect(() => {
    // Remove any intercepted forms data when moving away from the page
    return () => {
      setInterceptedFormsObject(undefined);
      setHasInterceptedForms(false);
      setIsPendingInteractions(false);
      setPendingInteractionFormData(undefined);
      setOriginalInterceptedForms([]);
    };
  }, [location.pathname]);

  useEffect(() => {
    getUserPermissionsQuery.data && setPermissions(getUserPermissionsQuery.data);
  }, [getUserPermissionsQuery.data]);

  useEffect(() => {
    submitInteractions && submitInteractionForms();
  }, [submitInteractions]);

  useEffect(() => {
    if (partTypeValue) {
      partTypeImagesQuery.refetch();
    }
  }, [partTypeValue]);

  useEffect(() => {
    if (partTypeImagesQuery.data) {
      const images: Array<ImagePopupSlide> = partTypeImagesQuery.data.map((obj) => ({
        url: obj.url
      }));
      setPartTypeImages(images);
    }
  }, [partTypeImagesQuery.data]);

  useEffect(() => {
    isPartActionPopup && getPartActionTypes.refetch();
  }, [isPartActionPopup]);

  useEffect(() => {
    isTask && getTaskTypes.refetch();
  }, [isTask]);

  useEffect(() => {
    setTaskTypes(getTaskTypes.data || []);
  }, [getTaskTypes.data]);

  useEffect(() => {
    interceptedFormsObject?.multiOnSiteSuggestionsAvailable && getCurrentUserAllocatedServices.refetch();
  }, [interceptedFormsObject]);

  useEffect(() => {
    getCurrentUserAllocatedServices.data && interceptedFormsObject?.multiOnSiteSuggestionsAvailable && setAllocatedServices(getCurrentUserAllocatedServices.data);
  }, [getCurrentUserAllocatedServices.data, interceptedFormsObject]);

  useEffect(() => {
    setOnSiteTime(getCurrentTime());
  
    if (service?.travelStartTime) {
      const dateObj = getDateFromTimestamp(service.travelStartTime);
      dateObj && setTravelStart(dateObj);
    }
  
    if (service.id) {
      getServiceCheckInQuery.refetch();
    }
  }, [service]);

  const receiveShipment = async (serializeManifestId?: number, shipmentManifestSummery?: ShipmentManifestSummery) => {
    if(shipmentManifestSummery?.shipmentId) {
      if(serializeManifestId) {
        const serializeManifest = shipmentManifestSummery.serializedShipmentManifestItem.find(manifest => manifest.manifestId === serializeManifestId);

        if(serializeManifest) {
          serializeManifest.selected = true;
        }
      }
      else {
        shipmentManifestSummery.serializedShipmentManifestItem.map(manifest => manifest.selected = true);
        shipmentManifestSummery.nonSerializedShipmentManifestItem.map(manifest => manifest.selected = true );
      }

      let shipmentNote = `${formatDateSimple(new Date().toString())} Shipment (${shipmentManifestSummery.shipmentId}) received by ${shipmentManifestSummery.siblingNodeName} by scanning Serial ${shipmentManifestSummery.serial} on Service ID ${service.id}.`;
      shipmentNote = `${shipmentManifestSummery.shipmentNote} ${shipmentNote}`.trim();
      await createShipmentIndividualReceive.mutateAsync({ ...shipmentManifestSummery, shipmentNote });
    }
  };

  const handleOnSubmitInteractionForms = async () => {
    let hasErrorFields = false;
    let hasByPassedFields = false;
    let blockInteractionBypassed = false;
    const updatedForms: FormObject[] = [];

    // isMandatory & isRequired validation
    interceptedFormsObject?.forms.forEach((form) => {
      const byPassedFormFields: string[] = [];
      const updatedContainers: FormContainerItem[] = [];
      form.containers.forEach((container) => {
        const updatedFields: FormFieldItem[] = [];
        container.fields.forEach((field) => {
          const updatedFieldObject = {
            ...field,
            isError: checkFieldIsError(permissions || [], form, container, field, service)
          };
          hasErrorFields = updatedFieldObject.isError || hasErrorFields;
          updatedFieldObject.isError && field.type === FormFieldType.ServiceCreation && (!container.isDecisionPrompt || container.questionData?.isYes) && setShowWarningToast(true);
          updatedFields.push(updatedFieldObject);
          if (checkIsByPassed(permissions || [], form, container, field, service)) {
            byPassedFormFields.push(field.title || field.type);
            hasByPassedFields = true;
          }
          if (isUserHasPermission(ActionPermissions.Configure_Interactions_Bypass_Mandatory, permissions) && field.type === FormFieldType.Block && !blockInteractionBypassed) {
            blockInteractionBypassed = true;
          }
        });
        updatedContainers.push({ ...container, fields: updatedFields });
      });
      updatedForms.push({ ...form, containers: updatedContainers, byPassedFormFields, blockInteractionBypassed });
    });
    if (hasErrorFields && interceptedFormsObject) {
      setInterceptedFormsObject({ ...interceptedFormsObject, forms: updatedForms });
      pendingInteractionFormData && setPendingInteractionFormData(updatedForms);
      return;
    }
    interceptedFormsObject && (hasByPassedFields || blockInteractionBypassed) && setInterceptedFormsObject({ ...interceptedFormsObject, forms: updatedForms });
    setSubmitInteractions(true);
  };

  const submitInteractionForms = async (updatedInterceptedFormsObject?: InterceptedFormsObject) => {
    let newInterceptedFormsObject = (updatedInterceptedFormsObject || interceptedFormsObject) as InterceptedFormsObject;
    if (newInterceptedFormsObject) {
      setIsFormSubmitting(true);

      let photoKeys: any;
      if (merchantDamageObjectParcer?.serialData?.partId) {
        photoKeys = await uploadFiles(merchantDamageObjectParcer?.serialData?.partId, merchantDamageObjectParcer?.photos, Folders.PartImages);
        await uploadFiles(service.id, merchantDamageObjectParcer?.photos, Folders.ServiceAttachments, ServiceAttachmentUploadFeature.InteractionPhotoUpload);
      }

      let currentStatus = '';
      // Sub form activities
      const waitForSubFormActivities = new Promise<boolean>((resolve) => {
        (async () => {
          for (const form of newInterceptedFormsObject.forms) {
            for (const container of form.containers) {
              if (container.isDecisionPrompt && (container.questionData?.hasValue && !container.questionData?.isYes || !container.questionData?.hasValue)) {
                continue;
              }
              for (const field of container.fields) {
                // Create service if a Service Creation Form exist
                if (field.type === FormFieldType.ServiceCreation) {
                  if (field.value) {
                    const newServiceObj = {
                      ...field.value,
                      serviceStatusCode: 'new',
                      serviceTypeCode: field.value.serviceTypeCode,
                      actionTypeCode: field.value.actionTypeCode,
                      processTypeCode: field.value.processTypeCode,
                      clientHierarchyId: field.value.brandId > 0 ? field.value.brandId : field.value.clientId,
                      priorityCode: field.value.priorityCode,
                      subLocationNodeId: field.value.subLocationNodeId,
                      mainLocationNodeId: field.value.mainLocationNodeId,
                      scheduledAt: field.value.scheduledAt ? formatDate(field.value.scheduledAt) : null,
                      notes: field.value.notes,
                      serviceAttributes: getServiceAttributesForServiceCreationForm(field.value),
                      userDepotAllocationNodeId: field.value.userDepotAllocationNodeId,
                      elementForDisplaying: field.value.contactData
                    };

                    if (newServiceObj.priorityCode < 1 || newServiceObj.mainLocationNodeId < 1 || checkForSelectedRules(field.value)) {
                      updateErrorState(form, container, field);
                      return resolve(false);
                    } else {
                      const createdService: any = await addServiceQuery.mutateAsync(newServiceObj);
                      field.value = {
                        ...createdService,
                        id: createdService.id,
                        clientHierarchy: field.value.clientHierarchy,
                        client: field.value.client,
                        contract: field.value.contract,
                        brand: field.value.brand,
                        mainLocationNodeId: field.value.mainLocationNodeId,
                        subLocationNodeId: field.value?.subLocationNodeId,
                        referenceNo1: field.value.referenceNo1,
                        referenceNo2: field.value.referenceNo2,
                        referenceNo3: field.value.referenceNo3,
                        userDepotAllocation: field.value.userDepotAllocation,
                        scheduledAt: formatDate(field.value.scheduledAt, ''),
                        reportedSerialNumber: field.value?.reportedSerialNumber,
                        reportedModel: field.value?.reportedModel,
                        reportedFaultAndDescription: field.value?.reportedFaultAndDescription,
                        notes: field.value.notes,
                        tagCreationData: field.value.tagCreationData
                      };
                      setNewServiceId(createdService.id);

                      if (createdService) {
                        if (field.value.tagCreationData?.selectedTags?.length || field.value.tagCreationData?.newTags?.length) {
                          const existingTags = field.value.tagCreationData?.selectedTags ? getSelectedExistingTagsMapped(field.value.tagCreationData.selectedTags) : [];
                          const freshTags = field.value.tagCreationData?.newTags ? getNewTagsMapped(field.value.tagCreationData.newTags) : [];

                          await updateTagsQuery.mutateAsync({
                            entityTypeId: EntityType.TYPE_SERVICES,
                            entityId: createdService.id,
                            freshTags,
                            existingTags
                          });
                        }

                        await updateParentServiceQuery.mutateAsync({
                          id: service.id,
                          parentServiceId: createdService.id,
                          isChild: true
                        });
                      }
                    }
                  }
                }

                if (field.type === FormFieldType.ServiceCreation && form.byPassedFormFields?.includes(field.title || field.type)) {
                  field.value = undefined;
                }

                // Create sub location or deactivate nodes if a Modify Structure Form exists
                if (field.type === FormFieldType.ModifyStructure) {
                  if (field.preSelectedData?.structureType === ModifyStructureFormModifyStructureType.CreateLocation && !service.subLocationNodeId && !hasFormFieldValueError(field, service)) {
                    if (!field.value?.locationName || isEmptyString(field.value?.locationName)) {
                      updateErrorState(form, container, field);
                      return resolve(false);
                    } else {
                      const newSublocation = await createStructure.mutateAsync({
                        parentNodeId: service.mainLocationNodeId,
                        name: field.value?.locationName,
                        code: field.value?.locationName,
                        nodeTypeCode: field.value.modifyStructure.selectedValue,
                        isActive: true,
                        attributes: []
                      });

                      if (newSublocation) {
                        await updateServiceQuery.mutateAsync({
                          id: service.id,
                          serviceTypeCode: service.serviceTypeCode,
                          serviceStatusCode: service.serviceStatusCode,
                          actionTypeCode: service.actionTypeCode,
                          processTypeCode: service.processTypeCode,
                          nodeId: service.nodeId,
                          priorityCode: service.priorityCode,
                          referenceNo1: service.referenceNo1,
                          subLocationNodeId: newSublocation.id
                        });
                      }
                    }
                  } else if (field.preSelectedData?.structureType === ModifyStructureFormModifyStructureType.ToggleActive) {
                    await modifyStructuresToggleActive.mutateAsync({
                      serviceMainLocationNodeId: service.mainLocationNodeId,
                      serviceSubLocationNodeId: service.subLocationNodeId,
                      toggleActiveType: field.preSelectedData?.selectedValue
                    });
                  }
                }

                // Update parameters if a Parameter Via UI Form exist
                if (field.type === FormFieldType.SetParameter && !hasFormFieldValueError(field, service)) {
                // Status
                  if (field.preSelectedData?.parameterType === ParameterDataFormParameterType.Status) {
                    if (!field.value || isEmptyString(field.value)) {
                      updateErrorState(form, container, field);
                      return resolve(false);
                    } else {
                      currentStatus = service.serviceStatusCode;
                      const updatedService: any = await updateServiceQuery.mutateAsync({
                        id: service.id,
                        serviceTypeCode: service.serviceTypeCode,
                        serviceStatusCode: field.value,
                        actionTypeCode: service.actionTypeCode,
                        processTypeCode: service.processTypeCode,
                        nodeId: service.nodeId,
                        priorityCode: service.priorityCode,
                        referenceNo1: service.referenceNo1,
                        subLocationNodeId: service.subLocationNodeId
                      });
                      if (updatedService?.serviceStatusCode !== field.value) {
                        field.specialLog = `Status update to '${field.value}' through Set Parameter - Status interaction failed due to service status progression blocks`;
                      }
                    }
                  }

                  // PrimaryAllocation
                  if (field.preSelectedData?.parameterType === ParameterDataFormParameterType.PrimaryAllocation) {
                    if (!field.value?.id) {
                      updateErrorState(form, container, field);
                      return resolve(false);
                    } else {
                      const allocationData: ServiceNodeAllocationData = {
                        serviceId: service.id,
                        userDepotAllocationNodeId: field.value?.id,
                        allocationTypeCode: ServiceNodeAllocationType.Primary
                      };
                      await updateServiceAllocationQuery.mutateAsync(allocationData);
                    }
                  }

                  // SecondaryAllocation
                  if (field.preSelectedData?.parameterType === ParameterDataFormParameterType.SecondaryAllocation) {
                    if (!field.value?.id) {
                      updateErrorState(form, container, field);
                      return resolve(false);
                    } else {
                      const allocationData: ServiceNodeAllocationData = {
                        serviceId: service.id,
                        userDepotAllocationNodeId: field.value?.id,
                        allocationTypeCode: ServiceNodeAllocationType.Secondary
                      };
                      await updateServiceAllocationQuery.mutateAsync(allocationData);
                    }
                  }

                  // UpdateSubLocation
                  if (field.preSelectedData?.parameterType === ParameterDataFormParameterType.UpdateSubLocation) {
                    if (!field.value?.id) {
                      updateErrorState(form, container, field);
                      return resolve(false);
                    } else {
                      await updateServiceQuery.mutateAsync({
                        id: service.id,
                        serviceTypeCode: service.serviceTypeCode,
                        serviceStatusCode: service.serviceStatusCode,
                        actionTypeCode: service.actionTypeCode,
                        processTypeCode: service.processTypeCode,
                        nodeId: service.nodeId,
                        priorityCode: service.priorityCode,
                        referenceNo1: service.referenceNo1,
                        subLocationNodeId: field.value?.id
                      });
                    }
                  }

                  // ScheduledDate
                  if (field.preSelectedData?.parameterType === ParameterDataFormParameterType.ScheduledDate) {
                    if (!field.value) {
                      updateErrorState(form, container, field);
                      return resolve(false);
                    } else {
                      await updateServiceQuery.mutateAsync({
                        id: service.id,
                        serviceTypeCode: service.serviceTypeCode,
                        serviceStatusCode: service.serviceStatusCode,
                        actionTypeCode: service.actionTypeCode,
                        processTypeCode: service.processTypeCode,
                        nodeId: service.nodeId,
                        priorityCode: service.priorityCode,
                        referenceNo1: service.referenceNo1,
                        subLocationNodeId: service.subLocationNodeId,
                        scheduledAt: formatDate(field.value)
                      });
                    }
                  }

                  // ReportedSerial
                  if (field.preSelectedData?.parameterType === ParameterDataFormParameterType.ReportedSerial) {
                    if (!field.value?.name) {
                      updateErrorState(form, container, field);
                      return resolve(false);
                    } else {
                      await updateReportedInformation.mutateAsync({
                        id: service.id,
                        referenceNo1: service.referenceNo1,
                        referenceNo2: service.referenceNo2,
                        referenceNo3: service.referenceNo3,
                        reportedFaultAndDescriptionId: service.reportedFaultAndDescriptionId,
                        reportedModelId: service.reportedModelId,
                        reportedSerialNumber: field.value?.id // Sending ID is a must in here not name prop
                      });
                    }
                  }

                  // SetTags
                  if (field.preSelectedData?.parameterType === ParameterDataFormParameterType.SetTag) {
                    if ((field.value.selectedTags && field.value.selectedTags.length < 1) && (field.value.newTags && field.value.newTags.length < 1)) {
                      updateErrorState(form, container, field);
                      return resolve(false);
                    } else {
                      const existingTags = getSelectedExistingTagsMapped(field.value.selectedTags);
                      const freshTags = getNewTagsMapped(field.value.newTags);

                      await updateTagsQuery.mutateAsync({
                        entityTypeId: EntityType.TYPE_SERVICES,
                        entityId: service.id,
                        freshTags,
                        existingTags
                      });
                    }
                  }
                }

                if (field.type === FormFieldType.ServiceSearch) {
                  for (const service of field.value) {
                    if (service.isSelected) {
                      service.checkInTime = newInterceptedFormsObject.payload.checkInTime;
                      service.travelStartTime = newInterceptedFormsObject.payload.travelStartTime;
                      service.locationNodeId = newInterceptedFormsObject.payload.locationNodeId;
                      service.notes = newInterceptedFormsObject.payload.notes || noteText;
                    }
                  }
                }

                if (photoKeys && container.id === merchantDamageObjectParcer?.containerId && field.id === merchantDamageObjectParcer.fieldId) {
                  field.value.photoKeys = Object.values(photoKeys);
                  field.value = JSON.stringify(field.value);
                }
              }
            }
          }

          if (noteText && newInterceptedFormsObject.payload.note === '') {
            newInterceptedFormsObject.payload.note = noteText;
          }

          if (statusValue === ServiceStatusCode.OnSite) {
            let bulkCheckinData: Array<CheckInObject> = [];
            if (getServiceCheckInQuery.data) {
              bulkCheckinData = processBulkCreateData(
                allocatedServices,
                notCheckedInService,
                checkedInService,
                onSiteTime,
                travelStart,
                service,
                notesText
              );
            }
            const selectedServiceIds: number[] = checkedInService?.map((service) => service.serviceId);
            const checkinData = {
              serviceId: service.id,
              checkInTime: onSiteTime ? getJSTimestamp(onSiteTime) : getCurrentTimestamp(),
              travelStartTime: getTimestamp(travelStart),
              locationNodeId: service.mainLocationNodeId as number,
              notes: notesText,
              selectedServiceIds: selectedServiceIds,
              actualCheckInTime: onSiteTime ? getDateFromTimestamp(onSiteTime) : getCurrentTime()
            };
            newInterceptedFormsObject.payload.bulkCheckinData = bulkCheckinData;
            newInterceptedFormsObject.payload.checkinData = checkinData;
          }

          if (
            (service.serviceStatusCode === ServiceStatusCode.OnSite && statusValue !== '-1' && statusValue !== ServiceStatusCode.OnSite) ||
            (currentStatus === ServiceStatusCode.OnSite && service.serviceStatusCode !== ServiceStatusCode.OnSite) // For status change through Set Parameter interaction
          ) {
            const checkoutData = {
              checkinId: getCheckedInData(getServiceCheckInQuery.data!)?.id || 0,
              nextStatus: statusValue,
              notes: noteText,
              checkOutTime: getCurrentTimestamp(),
              actualCheckOutTime: getCurrentTime()
            };
            newInterceptedFormsObject.payload.checkoutData = checkoutData;
          }
        
          // Wait for 1000 milliseconds until the backend gets ready for the interactions
          setTimeout(() => {
            return resolve(true);
          }, 1000);
        })();
      });

      const success = await waitForSubFormActivities;
      if (success) {
        // For part action interactions
        if (isPartAction && updatedInterceptedFormsObject) {
          newInterceptedFormsObject = {
            ...newInterceptedFormsObject,
            payload: {
              partActionForms: mapPartActions(partActionCardDataset.filter((action) => action.value.id === updatedInterceptedFormsObject.forms[0].partActionId), service.id)
            }
          };
        };
        await submitFormsDataWithOriginalPayloadQuery.mutateAsync(newInterceptedFormsObject);
        if (newInterceptedFormsObject.actionTriggers?.some((action) => action.preSelectedData?.actionType === ActionTriggerFormActionType.Task)) {
          await getTasksByServiceIdQuery.refetch();
          getTasksByServiceIdQuery.data?.length && setIsTask(true);
        }
        if (newInterceptedFormsObject.actionTriggers?.some((action) => action.preSelectedData?.actionType !== ActionTriggerFormActionType.Task)) {
          await getPartActionsByServiceId.refetch();
          getPartActionsByServiceId.data?.length && setIsPartAction(true);
        }
        // For part action interactions
        if (isPartAction && updatedInterceptedFormsObject) {
          const filterInterceptedFormsObject = {
            ...interceptedFormsObject,
            payload: partActionCardDataset.filter((action) => action.value.id !== updatedInterceptedFormsObject.forms[0].partActionId),
            forms: interceptedFormsObject?.forms.filter((form) => form.partActionId !== updatedInterceptedFormsObject.forms[0].partActionId)
          };
          const updatedPartActionCardDataset = partActionCardDataset?.map((card) => {
            if (card.value.id === updatedInterceptedFormsObject.forms[0].partActionId) {
              card.executionCompleted = true;
            }
            return card;
          });
          if (updatedPartActionCardDataset.every((card) => card.executionCompleted)) {
            onPartActionsCancel(true);
          } else {
            setInterceptedFormsObject(filterInterceptedFormsObject as InterceptedFormsObject);
            onChangePartActionCard(updatedPartActionCardDataset);
          }
        } else {
          setInterceptedFormsObject(undefined);
        }
        setHasInterceptedForms(false);
        setIsPendingInteractions(false);
        setAnchorEl(null);
        await getPendingInteractionsByServiceId.refetch();
      }
      if (merchantDamageObjectParcer?.serialData?.partId) {
        await updatePartAdditionalData.mutateAsync({
          serviceId: service.id,
          photoKeys: Object.values(photoKeys).toString()
        });
      }
      setIsFormSubmitting(false);
      setSubmitInteractions(false);
      setOriginalInterceptedForms([]);
      setIsStatusChange(false);
      setAnchorEl(null);
      onNoteText('');
      setIsCancelOrReopen('');
      if (newInterceptedFormsObject?.isCheckin) {
        setInterceptedFormsObject(undefined);
        setNotesText('');
        setTravelStart('');
        setOnSiteTime('');
      }
    }
  };

  const updateErrorState = (form: FormObject, container: FormContainerItem, field: FormFieldItem) => {
    if (interceptedFormsObject) {
      const updatedFields: Array<FormFieldItem> = container.fields.map((obj) => obj.id === field.id ? {
        ...field,
        isError: true
      } : obj);

      const updatedContainers: Array<FormContainerItem> = form.containers.map((obj) => obj.id === container.id ? {
        ...container,
        fields: updatedFields
      } : obj);

      const updatedForms = interceptedFormsObject.forms.map((obj) => obj.id === form.id ? {
        ...form,
        containers: updatedContainers
      } : obj);

      setInterceptedFormsObject({ ...interceptedFormsObject, forms: updatedForms });
    }
  };

  const onPopupSave = async () => {
    setIsPartActionPopup(false);
    !partActionCardDataset?.length && setIsPartAction(true);
    if (VALID_PART_ACTION_TYPE_CODES.includes(partActionCardType)) {
      if (partActionCardDataset && partActionCardDataset.length > 0) {
        const sortedPartActionCards = partActionCardDataset.sort((a, b) => a?.value?.id - b?.value?.id);
        const index = sortedPartActionCards[sortedPartActionCards.length - 1].value.id;

        const initialValue = {
          id: index + 1,
          name: '',
          sw: '',
          hw: '',
          fw: '',
          ft: '',
          serial: '',
          qty: 1,
          condition: '',
          location: '',
          notes: '',
          type: partActionCardType,
          images: []
        };

        onChangePartActionCard([...partActionCardDataset, {
          value: initialValue,
          inValue: initialValue,
          outValue: initialValue
        }]);
      } else {
        const initialValue = {
          id: 1,
          name: '',
          sw: '',
          hw: '',
          fw: '',
          ft: '',
          serial: '',
          qty: 1,
          condition: '',
          location: '',
          notes: '',
          type: partActionCardType,
          images: []
        };
        onChangePartActionCard([...partActionCardDataset,
          {
            value: initialValue,
            inValue: initialValue,
            outValue: initialValue
          }
        ]);
      }
    }
  };

  const onPopupTaskSave = async () => {
    setIsTaskPopup(false);
    let newTaskCard: TaskCard = SAMPLE_TASK_CARD;
    taskCardDataset.sort((a, b) => a.id - b.id);
    const maxId = taskCardDataset.length > 0 ? taskCardDataset[taskCardDataset.length - 1].id : 0;

    // ToDo: This is a temporary fix. need to refactor this later
    const newId = maxId + 100;

    const taskTypeData: TaskTypes = taskTypes.find(data => data.code === taskType) || {} as TaskTypes;
    const { code: taskTypeCode, name: taskTypeName, category: taskTypeCategory, additionalData } = taskTypeData;

    newTaskCard = {
      ...newTaskCard,
      id: newId,
      serviceId: service.id,
      taskTypeCategory: taskTypeCategory,
      taskTypeCode: taskTypeCode,
      taskTypeName: taskTypeName,
      additionalData: additionalData,
      isNew: true
    };

    if (taskType === TaskType.LABOUR_TIME
      || taskType === TaskType.TRAVEL_TIME
      || taskType === TaskType.STAGING_TIME
      || taskType === TaskType.LOADING_TIME
    ) {
      newTaskCard = {
        ...newTaskCard,
        taskData: {
          duration: '00:00',
          notes: ''
        }
      };
    } else if (taskType === TaskType.TRAVEL_DISTANCE || taskType === TaskType.RESOURCE_UTILISED) {
      newTaskCard = {
        ...newTaskCard,
        taskData: {
          quantity: 0,
          notes: ''
        }
      };
    } else if (taskType === TaskType.QUOTE_VALUE) {
      newTaskCard = {
        ...newTaskCard,
        taskData: {
          valueCollection: 0,
          issuedTo: '',
          contactEmail: '',
          isApproved: false,
          notes: '',
        }
      };
    }
    
    taskCardDataset.push(newTaskCard);
  };
  
  useEffect(() => {
    if (getSuggestedNodesFromAllocationEngine.data && isUserHasPermission(ActionPermissions.Service_Edit_Allocation_Primary, getUserPermissionsQuery.data)) {
      const data = getSuggestedNodesFromAllocationEngine.data;
      data.forEach(item => item.isSuggestedNode = true);
      setSuggestedNodesFromAllocation(data);
    }
    else {
      setSuggestedNodesFromAllocation([]);
    }
  }, [getSuggestedNodesFromAllocationEngine.data, getUserPermissionsQuery.data]);

  useEffect(() => {
    if (interceptedFormsObject?.forms && interceptedFormsObject?.forms.length > 0 && ((!isPendingInteractions && checkSetParameterNoUIFormAvailability(interceptedFormsObject?.forms) &&
      (interceptedFormsObject?.payload?.execute || interceptedFormsObject?.payload?.execute === undefined)) ||
      checkGetGeoLocationFormAvailability(interceptedFormsObject?.forms) || checkModifyStructureToggleActiveFormAvailability(interceptedFormsObject?.forms))
    ) {
      setIsFormSubmitting(true);
      setTimeout(() => {
        handleOnSubmitInteractionForms();
      }, 1500);
    }
    if (interceptedFormsObject?.forms && !originalInterceptedForms.length) {
      const originalForms = [...interceptedFormsObject.forms];
      setOriginalInterceptedForms(originalForms);
    }
  }, [interceptedFormsObject?.forms]);

  useEffect(() => {
    if (interceptedFormsObject?.actionTriggers?.length && !interceptedFormsObject.forms.length && (isPartAction || isTask)) {
      setIsFormSubmitting(true);
      setTimeout(() => {
        handleOnSubmitInteractionForms();
      }, 1500);
    }
  }, [interceptedFormsObject]);

  useEffect(() => {
    isCancelOrReopen && getServiceCancelOrReopenStatuses.refetch();
    !isCancelOrReopen && queryClient.removeQueries({ queryKey: [CancelReOpenStatusKey] });
  }, [isCancelOrReopen]);

  useEffect(() => {
    isCancelOrReopen && getServiceCancelOrReopenStatuses.data && setIsStatusChange(!isStatusChange);
  }, [getServiceCancelOrReopenStatuses.data]);

  useEffect(() => {
    setTimeout(() => {
      !submitInteractions && newServiceId !== 0 && navigate(generateServiceURL(newServiceId));
    }, 1500);
  }, [submitInteractions]);

  useEffect(() => {
    if (interceptedFormsObject?.forms && repeatableFormData.templateId !== 0 && repeatableFormData.templateTitle !== '') {
      const newForm = originalInterceptedForms.find((form) => form.templateId === repeatableFormData.templateId && form.title === repeatableFormData.templateTitle);
      const updatedForms = !pendingInteractionFormData ? [...interceptedFormsObject.forms] : [...pendingInteractionFormData];
      newForm && updatedForms.push({ ...newForm, id: Math.max(...updatedForms.map((form) => form.id)) + 1 });
      setInterceptedFormsObject({ ...interceptedFormsObject, forms: updatedForms });
      pendingInteractionFormData && setPendingInteractionFormData(updatedForms);
    }
    (repeatableFormData.templateId !== 0 || repeatableFormData.templateTitle !== '') && setRepeatableFormData({ templateId: 0, templateTitle: '' });
  }, [repeatableFormData]);

  useEffect(() => {
    isStatusChange && onSubmit();
  }, [statusValue]);

  useEffect(() => {
    pendingInteractionFormData?.length && !isPendingInteractions && setIsPendingInteractions(true);
  }, [pendingInteractionFormData]);

  useEffect(() => {
    if (getPendingInteractionsByServiceId.data && !pendingInteractionFormData?.length) {
      getServiceLogsByServiceIdQuery.refetch();
      getPartActionsByServiceId.refetch();
      getTasksByServiceIdQuery.refetch();
    }
  }, [getPendingInteractionsByServiceId.data]);

  const onPartSave = async () => {
    if(partTypeValue && selectedPartActionData){
      const imageData: any[] = [];
      if (partImages) {
        if (partImages.deviceImage) {
          const deviceImageData = new FormData();
          deviceImageData.append('fileList', partImages.deviceImage.file);
          const deviceImageUploadResponse = await uploadFiles(service.id, deviceImageData, Folders.ServiceAttachments, ServiceAttachmentUploadFeature.PartActionPartCreation);
          if(deviceImageUploadResponse['0']?.dbSuccess){
            imageData.push({ ...deviceImageUploadResponse['0'], isDeviceImage: true });
          }
        }
        
        if (partImages.labelImage) {
          const serialImageData = new FormData();
          serialImageData.append('fileList', partImages.labelImage.file);
          const labelImageUploadResponse = await uploadFiles(service.id, serialImageData, Folders.ServiceAttachments, ServiceAttachmentUploadFeature.PartActionPartCreation);
          if(labelImageUploadResponse['0']?.dbSuccess){
            imageData.push({ ...labelImageUploadResponse['0'], isDeviceImage: false });
          }
        }
        setPartImages(undefined);
      }
      const part = {
        partTypeId: partTypeValue.partTypeId,
        serial1: selectedPartActionData.newPartSerial,
        conditionCode: selectedPartActionData.action == Action.IN ? PartConditionOptions.GOOD : PartConditionOptions.FAULTY,
        serviceId: service.id,
        exception: true,
        isAccepted: false,
        partActionType: selectedPartActionData.partActionType,
        imageData,
        isActive: false
      };

      // Save the details for the exceptional part creation temporarily
      if (pendingPartCardId !== null) {
        setPendingPartsToCreate(prev => {
          const copy = { ...(prev || {}) };
          const list = copy[pendingPartCardId] ? [...copy[pendingPartCardId]] : [];
          list.push({ payload: part, meta: { selectedPartActionData, partTypeValue } });
          copy[pendingPartCardId] = list;
          return copy;
        });
      }
      setPendingPartCardId(null);
      setOpenPartCreationPopup(false);
      onExceptionPartCreated(part);
    }
  };

  // Create the exceptional parts along with part action execution
  const createPendingPartForCard = async (cardId: number): Promise<CustomisePartActionCard | null> => {
    // Get details for the exceptional part creation by card id
    const entries = pendingPartsToCreate[cardId];
    if (!entries || !entries.length) return null;
    let lastUpdatedCard: CustomisePartActionCard | null = null;
    try {
      for (const entry of entries) {
        // Create exceptional part and update card dataset
        const response = await createPartQuery.mutateAsync(entry.payload);
        const updatedCard = onExceptionPartCreated(response, entry.meta);
        if (lastUpdatedCard && updatedCard) {
          updatedCard.outValue = lastUpdatedCard.outValue;
        }
        if (updatedCard) lastUpdatedCard = updatedCard;
      }
      // remove pending entries for processed parts
      setPendingPartsToCreate(prev => {
        const copy = { ...(prev || {}) };
        delete copy[cardId];
        return copy;
      });
      return lastUpdatedCard;
    } catch {
      setPendingPartsToCreate(prev => {
        const copy = { ...(prev || {}) };
        delete copy[cardId];
        return copy;
      });
      return null;
    }
  };

  const onExceptionPartCreated = (response: any, meta?: ExceptionalPartMetaData): CustomisePartActionCard | null => {
    const partActionData =
    { 
      ...partActionCardDataset.find((card: CustomisePartActionCard) => (card.value?.id === meta?.selectedPartActionData?.value.id) || (card.value?.id === selectedPartActionData?.value.id)),
      newPartSerial: meta?.selectedPartActionData?.newPartSerial || selectedPartActionData?.newPartSerial,
      isPartCreation: meta?.selectedPartActionData?.isPartCreation || selectedPartActionData?.isPartCreation,
      action: meta?.selectedPartActionData?.action || selectedPartActionData?.action,
      partActionType: meta?.selectedPartActionData?.partActionType || selectedPartActionData?.partActionType
    } as PartCreationOptionData;
    const partTypeData = meta?.partTypeValue || partTypeValue;
    if (!partActionData || !partTypeData) return null;

    const updatedPartData = {
      partTypeId: response.partTypeId,
      partId: response.id,
      name: partTypeData.name,
      category: partTypeData.categoryCode,
      condition: response.conditionCode,
      conditionName: partActionData.action === Action.IN ? PartConditionName.Good : PartConditionName.Faulty,
      serial: response.serial1,
      location: response.locationName,
      serviceId: service.id,
      hasException: response.exception || false,
      deviceImageS3Key: getExceptionPartImageFileKey(true, partActionData.action === Action.OUT, partActionData.value.id, partActionCardDataset, response.imageData),
      deviceImageFileName: getExceptionPartImageFileName(true, partActionData.action === Action.OUT, partActionData.value.id, partActionCardDataset, response.imageData),
      serialImageS3Key: getExceptionPartImageFileKey(false, partActionData.action === Action.OUT, partActionData.value.id, partActionCardDataset, response.imageData),
      serialImageFileName: getExceptionPartImageFileName(false, partActionData.action === Action.OUT, partActionData.value.id, partActionCardDataset, response.imageData),
      isExceptionalCreation: true
    };

    let partAction: any;

    if ([PartActionType.INSTALL, PartActionType.TEST, PartActionType.ADJUST, PartActionType.REPAIR].includes(partActionData.partActionType)) {
      partAction = {
        outValue: partActionData.outValue,
        inValue: partActionData.inValue,
        value: {
          ...partActionData.value,
          ...updatedPartData
        }
      };
    } else {
      if (partActionData.action === Action.IN) {
        partAction = {
          value: partActionData.value,
          inValue: {
            ...partActionData.inValue,
            id: partActionData.inValue?.id || 0,
            ...updatedPartData
          },
          outValue: partActionData.outValue
        };
      } else {
        partAction = {
          value: partActionData.value,
          outValue: {
            ...partActionData.outValue,
            id: partActionData.outValue?.id || 0,
            locationId: response.locationId,
            ...updatedPartData
          },
          inValue: partActionData.inValue
        };
      }
    }

    const index = partActionCardDataset.findIndex((obj) => obj.value.id === partAction.value.id);
    const newDataSet = partActionCardDataset.filter((obj) => obj.value.id !== partAction.value.id);

    const updatedCard: CustomisePartActionCard = {
      value: partAction.value,
      outValue: partAction.outValue || partAction.value,
      inValue: partAction.inValue || partAction.value
    };

    newDataSet.splice(index, 0, updatedCard);
    onChangePartActionCard(newDataSet);
    return updatedCard;
  };

  const handleSubmit = () => {
    if (isDropDownEmpty(statusValue)) {
      setValidate(true);
      return;
    }

    let checkinData: CheckInObject | undefined;
    let bulkCheckinData: Array<CheckInObject> = [];
    // Checkin data
    if (interceptedFormsObject?.isCheckin && statusValue === ServiceStatusCode.OnSite) {
      if (getServiceCheckInQuery.data) {
        bulkCheckinData = processBulkCreateData(
          allocatedServices,
          notCheckedInService,
          checkedInService,
          onSiteTime,
          travelStart,
          service,
          notesText
        );
      }
      const selectedServiceIds: number[] = bulkCheckinData?.map((checkin) => checkin.serviceId);
      checkinData = {
        serviceId: service.id,
        checkInTime: onSiteTime ? getJSTimestamp(onSiteTime) : getCurrentTimestamp(),
        travelStartTime: getTimestamp(travelStart),
        locationNodeId: service.mainLocationNodeId as number,
        notes: notesText,
        selectedServiceIds: selectedServiceIds,
        actualCheckInTime: onSiteTime ? getDateFromTimestamp(onSiteTime) : getCurrentTime()
      };
    }

    let checkoutData: CheckOutObject | undefined;
    // Checkout data
    if (service.serviceStatusCode === ServiceStatusCode.OnSite && statusValue !== '-1' && statusValue !== ServiceStatusCode.OnSite) {
      checkoutData = {
        checkinId: getCheckedInData(getServiceCheckInQuery.data!)?.id || 0,
        nextStatus: statusValue,
        notes: noteText,
        checkOutTime: getCurrentTimestamp(),
        actualCheckOutTime: getCurrentTime()
      };
    }

    setIsStatusChange(false);
    setIsCancelOrReopen('');
    onSubmit(true, checkinData, bulkCheckinData, checkoutData, interceptedFormsObject?.actionTriggers);
    setAnchorEl(null);
    onNoteText('');
    setHasInterceptedForms(false);
    onChangeStatusValue('-1');
    if (interceptedFormsObject?.isCheckin || interceptedFormsObject?.actionTriggers) {
      setInterceptedFormsObject(undefined);
      setNotesText('');
      setTravelStart('');
      setOnSiteTime('');
      setCheckedInService([]);
      setNotCheckedInService([]);
    }
  };

  const clickPendingInteractions = () => {
    if (getPendingInteractionsByServiceId.data?.length) {
      const formsWithDisplayOrder = getPendingInteractionsByServiceId.data.filter((form) => form.displayOrder);
      const formsWithoutDisplayOrder = getPendingInteractionsByServiceId.data.filter((form) => !form.displayOrder);
      setPendingInteractionFormData(getSortedPendingInteractions(formsWithDisplayOrder, formsWithoutDisplayOrder));
    }
  };

  const onPartActionMenuItemClick = () => {
    if (partActionCardDataset?.length > 0) {
      setIsPartAction(!isPartAction);
    } else {
      setIsPartActionPopup(true);
      setAnchorEl(null);
    }
  };

  const getPartActionHelperText = (helperTextId: number) => {
    return helperText?.find((text) => text.id === helperTextId) || SAMPLE_PART_ACTION_CARD_HELPER_TEXT;
  };

  const onSetPartActionHelperText = (updatedText: PartActionCardHelperText) => {
    setHelperText((prev) => {
      const exists = prev.some((item) => item.id === updatedText.id);

      if (exists) {
      // Update existing helper text
        return prev.map((item) =>
          item.id === updatedText.id ? { ...item, ...updatedText } : item
        );
      }

      // Create new helper text
      return [...prev, updatedText];
    });
  };

  const getPartActionInteractioForms = (partActionId: number) => {
    return {
      ...interceptedFormsObject,
      forms: interceptedFormsObject?.forms.filter((form) => form.partActionId === partActionId)
    } as InterceptedFormsObject;
  };

  const showPendingShipmentDialog = (shipment: ShipmentManifestSummery, index: number, total: number) => {
    return new Promise((resolve) => {
      const selectedPartAction = partActionCardDataset.find((card) => card.value.partId === shipment.partId);
      setSelectedPartActionCard(selectedPartAction);
      setShipmentManifestSummery(shipment);

      setPendingShipmentDialogData({
        currentNumber: index + 1,
        total,
        onOk: () => {
          setPendingShipmentDialogData(null);
          setOpenShipmentReceivePopup(false);
          resolve({ action: 'ok', shipment });
        },
        onCancel: () => {
          setPendingShipmentDialogData(null);
          setOpenShipmentReceivePopup(false);
          resolve({ action: 'cancel', shipment });
        }
      });

      setOpenShipmentReceivePopup(true);
    });
  };

  const onPartActionsBulkSubmit = async() => {
    setPartActionsExecuting(true);
    const updatedPartActionHelperTexts = validatePartActionHelperTexts(helperText, partActionCardDataset, service.serviceTypeCode);
    setHelperText(updatedPartActionHelperTexts);
    if(Object.values(pendingPartsToCreate).length > 0 && !isUserHasPermission(ActionPermissions.Service_Edit_Part_Actions_Exceptional_Creation, permissions)){
      setPartActionsExecuting(false);
      return;
    };

    const pendingPartIdsToCheck = getPendingPartIdsToCheck(partActionCardDataset);
    if (pendingPartIdsToCheck?.length > 0) {
      let pendingPartsShipmentsData = await getShipmentDetailsPartIncludes.mutateAsync({ serviceId: service.id, partIds: pendingPartIdsToCheck });
      if (pendingPartsShipmentsData?.length > 0) {
        pendingPartsShipmentsData = pendingPartsShipmentsData.map((shipment) => {
          shipment.partActionId = partActionCardDataset.find((card) => card.value.partId === shipment.partId)?.value.id;
          shipment.serial = partActionCardDataset.find((card) => card.value.partId === shipment.partId)?.value.serial || '';
          return shipment;
        });
        pendingPartsShipmentsData = pendingPartsShipmentsData.sort((shipment1, shipment2) => (shipment1.partActionId as number) - (shipment2.partActionId as number));

        // Receive shipments where sibling is not checked in/allocated to service or sibling is not under shipment destination node
        const directShipementReceivals = pendingPartsShipmentsData.filter((shipment) => !shipment.receiveEntireShipment);
        if (directShipementReceivals?.length > 0) {
          for (const shipmentReceive of directShipementReceivals) {
            await receiveShipment(shipmentReceive.partManifestId, shipmentReceive);
          }
        }

        // Pending shipments checks when sibling is checked in/allocated to service and sibling is under shipment destination node
        const pendingShipementReceivals = pendingPartsShipmentsData.filter((shipment) => shipment.receiveEntireShipment);
        if (pendingShipementReceivals?.length > 0) {
          for (let i = 0; i < pendingShipementReceivals.length; i++) {
            const shipment = pendingShipementReceivals[i];
            await showPendingShipmentDialog(shipment, i, pendingShipementReceivals.length);
          }
        }
      }
    }

    let preProcessedPartActions = await preProcessPartActions(partActionCardDataset, createPendingPartForCard, service.serviceTypeCode);
    let postExecutionPartActionsDataset: CustomisePartActionCard[] | undefined = [];
    if (preProcessedPartActions?.some((partAction) => partAction?.execute)) {
      const pendingShipmentsData = pendingShipmentsDataRef.current;
      if (pendingShipmentsData?.canceled && pendingShipmentsData?.canceled?.length > 0) {
        preProcessedPartActions = preProcessedPartActions.map((action) => {
          if (pendingShipmentsData?.canceled.some((card) => card.value.id === action.value.id )) {
            action.execute = false;
          }
          return action;
        });
      }
      const executablePartActions = preProcessedPartActions.filter((partAction) => partAction.execute);
      const nonExecutablePartActions = preProcessedPartActions.filter((partAction) => !partAction.execute);
      if (executablePartActions?.length > 0) {
        postExecutionPartActionsDataset = await onExecPartActions(executablePartActions, nonExecutablePartActions);
        if (!postExecutionPartActionsDataset?.length || postExecutionPartActionsDataset?.every((partAction) => partAction.executionCompleted)) {
          onPartActionsCancel(true);
        }
      }
    }
    setPartActionsExecuting(false);
    pendingShipmentsDataRef.current = { accepted: [], canceled: [] };
    setTimeout(() => {
      getServiceLogsByServiceIdQuery.refetch();
    }, 2000);
  };

  const onPartActionsCancel = (allPartActionsCompleted?: boolean) => {
    setIsPartAction(false);
    setInterceptedFormsObject(undefined);
    setHasInterceptedForms(false);
    setOriginalInterceptedForms([]);
    setPartActionsExecuting(false);
    setAnchorEl(null);
    setHelperText([SAMPLE_PART_ACTION_CARD_HELPER_TEXT]);
    setPendingShipmentDialogData(null);
    setSelectedPartActionCard(undefined);
    setShipmentManifestSummery(undefined);
    pendingShipmentsDataRef.current = { accepted: [], canceled: [] };
    if (allPartActionsCompleted) {
      onChangePartActionCard([]);
    } else {
      const notExecutedPartActionCardDataset = partActionCardDataset?.filter((card) => !card.executionCompleted);
      onChangePartActionCard(notExecutedPartActionCardDataset);
    }
  };

  const onConsumablePartActionCopy = (partActionData: PartActionCard) => {
    const sortedPartActionCards = partActionCardDataset.sort((a, b) => a?.value?.id - b?.value?.id);
    const index = sortedPartActionCards[sortedPartActionCards.length - 1].value.id;
    const cardData = {
      ...partActionData,
      id: index + 1,
      qty: 1,
      notes: '',
      ns_partId: NaN,
      location: '',
      condition: '',
      conditionName: '',
      ft: ''
    };
    onChangePartActionCard([...partActionCardDataset, {
      value: cardData,
      inValue: cardData,
      outValue: cardData
    }]);
  };

  return (
    <ContentPaper elevation={1}>
      {isStatusChange || isTask || isPendingInteractions ?
        <CancelButtonWrapStack>
          <Button variant={ButtonVariantProps.Secondary} color={ButtonColorProps.Error} className="extra-small-button"
            size={ButtonSizeProps.Small}
            onClick={() => {
              if (isStatusChange) {
                setIsStatusChange(!isStatusChange);
                setIsCancelOrReopen('');
                onNoteText('');
                setInterceptedFormsObject(undefined);
                setHasInterceptedForms(false);
                setIsFormSubmitting(false);
                setOriginalInterceptedForms([]);
                onChangeStatusValue('-1');
              }
    
              if (isTask) {
                setIsTask(false);
                setInterceptedFormsObject(undefined);
                setHasInterceptedForms(false);
                setOriginalInterceptedForms([]);
              }
    
              if (isPendingInteractions) {
                setIsPendingInteractions(false);
                setPendingInteractionFormData(undefined);
                setInterceptedFormsObject(undefined);
                setHasInterceptedForms(false);
                setOriginalInterceptedForms([]);
              }
    
              setAnchorEl(null);
            }}
          >
            Cancel
          </Button>
        </CancelButtonWrapStack>
        :
        <></>
      }

      <ActionBarStack direction="row" isStatusChange={isStatusChange} justifyContent="space-between" alignItems="center" spacing={2} width="100%">
        <Stack width="100%">
          {isTask && taskTypes.length ? (
            <Grid container rowSpacing={2} columnSpacing={{ sm: 4 }} pr={1}>
              {taskCardDataset.sort((a, b) => a.id - b.id).map((card: TaskCard, index: number) => (
                <Grid
                  key={index}
                  size={{
                    xs: 11.5,
                    sm: 12,
                    md: 6,
                    xl: 4
                  }}>
                  {(
                    card.taskTypeCode === TaskType.LABOUR_TIME ||
                    card.taskTypeCode === TaskType.TRAVEL_TIME ||
                    card.taskTypeCode === TaskType.STAGING_TIME ||
                    card.taskTypeCode === TaskType.LOADING_TIME
                  ) && (
                    <TaskTimeCard
                      data={card}
                      onChange={(taskCardObject: TaskCard) => onChangeTaskCard(taskCardObject)}
                      onSave={() => onSaveTaskCard(card)}
                      onExec={() => onExecTaskCard(card)}
                      onClose={() => onCloseAndChangePopupTaskCard(card)}
                    />
                  )}
                  {(
                    card.taskTypeCode === TaskType.TRAVEL_DISTANCE ||
                    card.taskTypeCode === TaskType.RESOURCE_UTILISED
                  ) && (
                    <TaskQuantityCard
                      data={card}
                      onChange={(taskCardObject: TaskCard) => onChangeTaskCard(taskCardObject)}
                      onSave={() => onSaveTaskCard(card)}
                      onExec={() => onExecTaskCard(card)}
                      onClose={() => onCloseAndChangePopupTaskCard(card)}
                    />
                  )}
                  {card.taskTypeCode === TaskType.QUOTE_VALUE && (
                    <TaskQuoteValueCard
                      data={card}
                      onChange={(taskCardObject: TaskCard) => onChangeTaskCard(taskCardObject)}
                      onSave={() => onSaveTaskCard(card)}
                      onExec={() => onExecTaskCard(card)}
                      onClose={() => onCloseAndChangePopupTaskCard(card)}
                    />
                  )}
                  {
                    card.taskTypeCategory === TaskTypeCategory.ServiceSpecial && (
                      <TaskServiceSpecialCard 
                        data={{ ...card, additionalData: taskTypes.find(data => data.code == card.taskTypeCode)?.additionalData }}
                        onChange={(taskCardObject: TaskCard) => onChangeTaskCard(taskCardObject)}
                        onSave={() => onSaveTaskCard(card)}
                        onExec={(taskData) => onExecTaskCard(taskData)}
                        onClose={() => onCloseAndChangePopupTaskCard(card)}
                      />
                    )
                  }
                </Grid>
              ))}
              <Grid
                size={{
                  xs: 12,
                  sm: 12,
                  md: 6,
                  xl: 4
                }}>
                <AddButton
                  startIcon={<AddOutlined />}
                  variant={ButtonVariantProps.Secondary}
                  color={ButtonColorProps.Success}
                  onClick={() => setIsTaskPopup(true)}
                  fullWidth
                > </AddButton>
              </Grid>
              <PopupDialog
                width="85%"
                maxWidth="400px"
                title="Tasks"
                isOpen={isTaskPopup}
                onClose={(event?: any, reason?: string) => (reason !== 'backdropClick') && setIsTaskPopup(false)}
                onSave={onPopupTaskSave}
              >
                <StyledSelectMenu
                  id="tasks-menu"
                  label="Select Task"
                  minWidth="100%"
                  items={getTaskTypes.data?.map(data => ({ value: data.code, label: data.name })) || []}
                  defaultSelectedValue={taskType}
                  onChange={(type: string) => setTaskType(type)}
                />
              </PopupDialog>
            </Grid>
          ) : isPartAction ? (
            <>
              <PartActionContainer isMobileView={isMobileView}>
                {partActionCardDataset.map((card) => (
                  <PartActionCardWrapper key={card.value.id}>
                    {card.value.type === PartActionType.INSTALL && (
                      <PartActionInstallCard
                        service={service}
                        value={card.value}
                        onChange={(val: CustomisePartActionCard | PartCreationOptionData, isTextChange?: boolean) => {
                          if ((val as any).isPartCreation) {
                            setSelectedPartActionData(val as PartCreationOptionData);
                            setPartTypeSearchValue(undefined);
                            setPendingPartCardId(card.value.id);
                            setOpenPartCreationPopup(true);
                          } else {
                            val = val as CustomisePartActionCard;
                            const index = partActionCardDataset.findIndex((obj) => obj.value.id === card.value.id);
                            const newDataSet = partActionCardDataset.filter((obj) => obj.value.id !== card.value.id);

                            const updatedCard: CustomisePartActionCard = {
                              value: val.value,
                              inValue: card.inValue,
                              outValue: card.outValue
                            };

                            newDataSet.splice(index, 0, updatedCard);
                            onChangePartActionCard(newDataSet, isTextChange);
                          }
                        } }
                        onClose={() => {
                        // remove pending part creation entries if part action closed
                          setPendingPartsToCreate(prev => {
                            const copy = { ...(prev || {}) };
                            delete copy[card.value.id];
                            return copy;
                          });
                          handlePartActionCardClose(card);
                        } }
                        helperText={getPartActionHelperText(card.value.id)}
                        setHelperText={(updatedText) => onSetPartActionHelperText({ ...updatedText, id: card.value.id })}
                        isMobileView={isMobileView}
                        interceptedFormsObject={getPartActionInteractioForms(card.value.id)}
                        setInterceptedFormsObject={(updatedInterceptedFormsObject) => {
                          const updatedObject = {
                            ...updatedInterceptedFormsObject,
                            forms: interceptedFormsObject?.forms.map((form) => {
                              if (form.partActionId === card.value.id) {
                                form = updatedInterceptedFormsObject?.forms?.find((updatedForm) => updatedForm.partActionId === card.value.id && updatedForm.id === form.id) as FormObject;
                              }
                              return form;
                            })
                          };
                          setInterceptedFormsObject(updatedObject as InterceptedFormsObject);
                        }}
                        repeatableFormData={repeatableFormData}
                        setRepeatableFormData={setRepeatableFormData}
                        setMerchantDamageObjectParcer={setMerchantDamageObjectParcer}
                        partActionData={card}
                        isInteractionSubmiting={isFormSubmitting}
                        submitInteractionForms={submitInteractionForms}
                        onConsumablePartActionCopy={onConsumablePartActionCopy}
                      />
                    )}
                    {card.value?.type === PartActionType.REPLACE &&
                      <PartActionReplaceCard
                        service={service}
                        value={card}
                        onChange={(customisedPartAction: CustomisePartActionCard | PartCreationOptionData, isTextChange?: boolean) => {
                          if ((customisedPartAction as any).isPartCreation) {
                            setSelectedPartActionData(customisedPartAction as PartCreationOptionData);
                            setPartTypeSearchValue(undefined);
                            setPendingPartCardId(card.value.id);
                            setOpenPartCreationPopup(true);
                          } else {
                            customisedPartAction = customisedPartAction as CustomisePartActionCard;
                            const partActionCardIndex = partActionCardDataset.findIndex((obj) => obj.value.id === card.value.id);
                            const filteredPartActionCardDataset = partActionCardDataset.filter((obj) => obj.value.id !== card.value.id);

                            const updatedCard: CustomisePartActionCard = {
                              value: card.value,
                              outValue: customisedPartAction.outValue || card.value,
                              inValue: customisedPartAction.inValue || card.value
                            };

                            filteredPartActionCardDataset.splice(partActionCardIndex, 0, updatedCard);
                            onChangePartActionCard(filteredPartActionCardDataset, isTextChange);
                          }
                        } }
                        onClose={() => {
                          // remove pending part creation entries if part action closed
                          setPendingPartsToCreate(prev => {
                            const copy = { ...(prev || {}) };
                            delete copy[card.value.id];
                            return copy;
                          });
                          handlePartActionCardClose(card);
                        } }
                        helperText={getPartActionHelperText(card.value.id)}
                        setHelperText={(updatedText) => onSetPartActionHelperText({ ...updatedText, id: card.value.id })}
                        isMobileView={isMobileView}
                        interceptedFormsObject={getPartActionInteractioForms(card.value.id)}
                        setInterceptedFormsObject={(updatedInterceptedFormsObject) => {
                          const updatedObject = {
                            ...updatedInterceptedFormsObject,
                            forms: interceptedFormsObject?.forms.map((form) => {
                              if (form.partActionId === card.value.id) {
                                form = updatedInterceptedFormsObject?.forms?.find((updatedForm) => updatedForm.partActionId === card.value.id && updatedForm.id === form.id) as FormObject;
                              }
                              return form;
                            })
                          };
                          setInterceptedFormsObject(updatedObject as InterceptedFormsObject);
                        }}
                        repeatableFormData={repeatableFormData}
                        setRepeatableFormData={setRepeatableFormData}
                        setMerchantDamageObjectParcer={setMerchantDamageObjectParcer}
                        partActionData={card}
                        isInteractionSubmiting={isFormSubmitting}
                        submitInteractionForms={submitInteractionForms}
                      />}
                    {card.value?.type === PartActionType.REMOVE &&
                      <PartActionRemoveCard
                        service={service}
                        value={card}
                        onChange={(val: CustomisePartActionCard | PartCreationOptionData, isTextChange?: boolean) => {
                          if ((val as any).isPartCreation) {
                            setSelectedPartActionData(val as PartCreationOptionData);
                            setPartTypeSearchValue(undefined);
                            setPendingPartCardId(card.value.id);
                            setOpenPartCreationPopup(true);
                          } else {
                            val = val as CustomisePartActionCard;
                            const index = partActionCardDataset.findIndex((obj) => obj.value.id === card.value.id);
                            const newDataSet = partActionCardDataset.filter((obj) => obj.value.id !== card.value.id);

                            const updatedCard: CustomisePartActionCard = {
                              value: card.value,
                              outValue: val.outValue || card.value,
                              inValue: val.inValue || card.value
                            };

                            newDataSet.splice(index, 0, updatedCard);
                            onChangePartActionCard(newDataSet, isTextChange);
                          }
                        } }
                        onClose={() => {
                          // remove pending part creation entries if part action closed
                          setPendingPartsToCreate(prev => {
                            const copy = { ...(prev || {}) };
                            delete copy[card.value.id];
                            return copy;
                          });
                          handlePartActionCardClose(card);
                        } }
                        helperText={getPartActionHelperText(card.value.id)}
                        setHelperText={(updatedText) => onSetPartActionHelperText({ ...updatedText, id: card.value.id })}
                        isMobileView={isMobileView}
                        interceptedFormsObject={getPartActionInteractioForms(card.value.id)}
                        setInterceptedFormsObject={(updatedInterceptedFormsObject) => {
                          const updatedObject = {
                            ...updatedInterceptedFormsObject,
                            forms: interceptedFormsObject?.forms.map((form) => {
                              if (form.partActionId === card.value.id) {
                                form = updatedInterceptedFormsObject?.forms?.find((updatedForm) => updatedForm.partActionId === card.value.id && updatedForm.id === form.id) as FormObject;
                              }
                              return form;
                            })
                          };
                          setInterceptedFormsObject(updatedObject as InterceptedFormsObject);
                        }}
                        repeatableFormData={repeatableFormData}
                        setRepeatableFormData={setRepeatableFormData}
                        setMerchantDamageObjectParcer={setMerchantDamageObjectParcer}
                        partActionData={card}
                        isInteractionSubmiting={isFormSubmitting}
                        submitInteractionForms={submitInteractionForms}
                      />
                    }
                    {card.value?.type === PartActionType.CLEAN && (
                      <PartActionCleanedCard
                        title="Cleaned"
                        service={service}
                        value={card.value}
                        onChange={(val: CustomisePartActionCard, isTextChange?: boolean) => {
                          const index = partActionCardDataset.findIndex((obj) => obj.value.id === card.value.id);
                          const newDataSet = partActionCardDataset.filter((obj) => obj.value.id !== card.value.id);

                          const updatedCard: CustomisePartActionCard = {
                            value: val.value,
                            inValue: card.inValue,
                            outValue: card.outValue
                          };

                          newDataSet.splice(index, 0, updatedCard);
                          onChangePartActionCard(newDataSet, isTextChange);
                        } }
                        onClose={() => handlePartActionCardClose(card)}
                        helperText={getPartActionHelperText(card.value.id)}
                        setHelperText={(updatedText) => onSetPartActionHelperText({ ...updatedText, id: card.value.id })}
                        isMobileView={isMobileView}
                        interceptedFormsObject={getPartActionInteractioForms(card.value.id)}
                        setInterceptedFormsObject={(updatedInterceptedFormsObject) => {
                          const updatedObject = {
                            ...updatedInterceptedFormsObject,
                            forms: interceptedFormsObject?.forms.map((form) => {
                              if (form.partActionId === card.value.id) {
                                form = updatedInterceptedFormsObject?.forms?.find((updatedForm) => updatedForm.partActionId === card.value.id && updatedForm.id === form.id) as FormObject;
                              }
                              return form;
                            })
                          };
                          setInterceptedFormsObject(updatedObject as InterceptedFormsObject);
                        }}
                        repeatableFormData={repeatableFormData}
                        setRepeatableFormData={setRepeatableFormData}
                        setMerchantDamageObjectParcer={setMerchantDamageObjectParcer}
                        partActionData={card}
                        isInteractionSubmiting={isFormSubmitting}
                        submitInteractionForms={submitInteractionForms}
                      />
                    )}
                    {card.value?.type === PartActionType.TEST && (
                      <PartActionTestedCard
                        title="Tested"
                        service={service}
                        value={card.value}
                        onChange={(val: CustomisePartActionCard | PartCreationOptionData, isTextChange?: boolean) => {
                          if ((val as any).isPartCreation) {
                            setSelectedPartActionData(val as PartCreationOptionData);
                            setPartTypeSearchValue(undefined);
                            setPendingPartCardId(card.value.id);
                            setOpenPartCreationPopup(true);
                          } else {
                            val = val as CustomisePartActionCard;
                            const index = partActionCardDataset.findIndex((obj) => obj.value.id === card.value.id);
                            const newDataSet = partActionCardDataset.filter((obj) => obj.value.id !== card.value.id);

                            const updatedCard: CustomisePartActionCard = {
                              value: val.value,
                              inValue: card.inValue,
                              outValue: card.outValue
                            };

                            newDataSet.splice(index, 0, updatedCard);
                            onChangePartActionCard(newDataSet, isTextChange);
                          }
                        } }
                        onClose={() => {
                        // remove pending part creation entries if part action closed
                          setPendingPartsToCreate(prev => {
                            const copy = { ...(prev || {}) };
                            delete copy[card.value.id];
                            return copy;
                          });
                          handlePartActionCardClose(card);
                        } }
                        helperText={getPartActionHelperText(card.value.id)}
                        setHelperText={(updatedText) => onSetPartActionHelperText({ ...updatedText, id: card.value.id })}
                        isMobileView={isMobileView}
                        interceptedFormsObject={getPartActionInteractioForms(card.value.id)}
                        setInterceptedFormsObject={(updatedInterceptedFormsObject) => {
                          const updatedObject = {
                            ...updatedInterceptedFormsObject,
                            forms: interceptedFormsObject?.forms.map((form) => {
                              if (form.partActionId === card.value.id) {
                                form = updatedInterceptedFormsObject?.forms?.find((updatedForm) => updatedForm.partActionId === card.value.id && updatedForm.id === form.id) as FormObject;
                              }
                              return form;
                            })
                          };
                          setInterceptedFormsObject(updatedObject as InterceptedFormsObject);
                        }}
                        repeatableFormData={repeatableFormData}
                        setRepeatableFormData={setRepeatableFormData}
                        setMerchantDamageObjectParcer={setMerchantDamageObjectParcer}
                        partActionData={card}
                        isInteractionSubmiting={isFormSubmitting}
                        submitInteractionForms={submitInteractionForms}
                      />
                    )}
                    {card.value?.type === PartActionType.DOA &&
                      <PartActionDoaCard
                        service={service}
                        value={card}
                        onChange={(val: CustomisePartActionCard, isTextChange?: boolean) => {
                          const index = partActionCardDataset.findIndex((obj) => obj.value.id === card.value.id);
                          const newDataSet = partActionCardDataset.filter((obj) => obj.value.id !== card.value.id);

                          const updatedCard: CustomisePartActionCard = {
                            value: card.value,
                            outValue: val.outValue || card.value,
                            inValue: val.inValue || card.value
                          };

                          newDataSet.splice(index, 0, updatedCard);
                          onChangePartActionCard(newDataSet, isTextChange);
                        } }
                        onClose={() => handlePartActionCardClose(card)}
                        helperText={getPartActionHelperText(card.value.id)}
                        setHelperText={(updatedText) => onSetPartActionHelperText({ ...updatedText, id: card.value.id })}
                        isMobileView={isMobileView}
                        interceptedFormsObject={getPartActionInteractioForms(card.value.id)}
                        setInterceptedFormsObject={(updatedInterceptedFormsObject) => {
                          const updatedObject = {
                            ...updatedInterceptedFormsObject,
                            forms: interceptedFormsObject?.forms.map((form) => {
                              if (form.partActionId === card.value.id) {
                                form = updatedInterceptedFormsObject?.forms?.find((updatedForm) => updatedForm.partActionId === card.value.id && updatedForm.id === form.id) as FormObject;
                              }
                              return form;
                            })
                          };
                          setInterceptedFormsObject(updatedObject as InterceptedFormsObject);
                        }}
                        repeatableFormData={repeatableFormData}
                        setRepeatableFormData={setRepeatableFormData}
                        setMerchantDamageObjectParcer={setMerchantDamageObjectParcer}
                        partActionData={card}
                        isInteractionSubmiting={isFormSubmitting}
                        submitInteractionForms={submitInteractionForms}
                      />}
                    {card.value.type === PartActionType.REPAIR && (
                      <PartActionRepairCard
                        service={service}
                        value={card.value}
                        onChange={(val: CustomisePartActionCard | PartCreationOptionData, isTextChange?: boolean) => {
                          if ((val as any).isPartCreation) {
                            setSelectedPartActionData(val as PartCreationOptionData);
                            setPartTypeSearchValue(undefined);
                            setPendingPartCardId(card.value.id);
                            setOpenPartCreationPopup(true);
                          } else {
                            val = val as CustomisePartActionCard;
                            const index = partActionCardDataset.findIndex((obj) => obj.value.id === card.value.id);
                            const newDataSet = partActionCardDataset.filter((obj) => obj.value.id !== card.value.id);

                            const updatedCard: CustomisePartActionCard = {
                              value: val.value,
                              inValue: card.inValue,
                              outValue: card.outValue
                            };

                            newDataSet.splice(index, 0, updatedCard);
                            onChangePartActionCard(newDataSet, isTextChange);
                          }
                        } }
                        onClose={() => {
                        // remove pending part creation entries if part action closed
                          setPendingPartsToCreate(prev => {
                            const copy = { ...(prev || {}) };
                            delete copy[card.value.id];
                            return copy;
                          });
                          handlePartActionCardClose(card);
                        } }
                        helperText={getPartActionHelperText(card.value.id)}
                        setHelperText={(updatedText) => onSetPartActionHelperText({ ...updatedText, id: card.value.id })}
                        isMobileView={isMobileView}
                        interceptedFormsObject={getPartActionInteractioForms(card.value.id)}
                        setInterceptedFormsObject={(updatedInterceptedFormsObject) => {
                          const updatedObject = {
                            ...updatedInterceptedFormsObject,
                            forms: interceptedFormsObject?.forms.map((form) => {
                              if (form.partActionId === card.value.id) {
                                form = updatedInterceptedFormsObject?.forms?.find((updatedForm) => updatedForm.partActionId === card.value.id && updatedForm.id === form.id) as FormObject;
                              }
                              return form;
                            })
                          };
                          setInterceptedFormsObject(updatedObject as InterceptedFormsObject);
                        }}
                        repeatableFormData={repeatableFormData}
                        setRepeatableFormData={setRepeatableFormData}
                        setMerchantDamageObjectParcer={setMerchantDamageObjectParcer}
                        partActionData={card}
                        isInteractionSubmiting={isFormSubmitting}
                        submitInteractionForms={submitInteractionForms}
                      />
                    )}
                    {card.value.type === PartActionType.ADJUST && (
                      <PartActionAdjustCard
                        service={service}
                        value={card.value}
                        onChange={(val: CustomisePartActionCard | PartCreationOptionData, isTextChange?: boolean) => {
                          if ((val as any).isPartCreation) {
                            setSelectedPartActionData(val as PartCreationOptionData);
                            setPartTypeSearchValue(undefined);
                            setPendingPartCardId(card.value.id);
                            setOpenPartCreationPopup(true);
                          } else {
                            val = val as CustomisePartActionCard;
                            const index = partActionCardDataset.findIndex((obj) => obj.value.id === card.value.id);
                            const newDataSet = partActionCardDataset.filter((obj) => obj.value.id !== card.value.id);

                            const updatedCard: CustomisePartActionCard = {
                              value: val.value,
                              inValue: card.inValue,
                              outValue: card.outValue
                            };

                            newDataSet.splice(index, 0, updatedCard);
                            onChangePartActionCard(newDataSet, isTextChange);
                          }
                        } }
                        onClose={() => {
                        // remove pending part creation entries if part action closed
                          setPendingPartsToCreate(prev => {
                            const copy = { ...(prev || {}) };
                            delete copy[card.value.id];
                            return copy;
                          });
                          handlePartActionCardClose(card);
                        } }
                        helperText={getPartActionHelperText(card.value.id)}
                        setHelperText={(updatedText) => onSetPartActionHelperText({ ...updatedText, id: card.value.id })}
                        isMobileView={isMobileView}
                        interceptedFormsObject={getPartActionInteractioForms(card.value.id)}
                        setInterceptedFormsObject={(updatedInterceptedFormsObject) => {
                          const updatedObject = {
                            ...updatedInterceptedFormsObject,
                            forms: interceptedFormsObject?.forms.map((form) => {
                              if (form.partActionId === card.value.id) {
                                form = updatedInterceptedFormsObject?.forms?.find((updatedForm) => updatedForm.partActionId === card.value.id && updatedForm.id === form.id) as FormObject;
                              }
                              return form;
                            })
                          };
                          setInterceptedFormsObject(updatedObject as InterceptedFormsObject);
                        }}
                        repeatableFormData={repeatableFormData}
                        setRepeatableFormData={setRepeatableFormData}
                        setMerchantDamageObjectParcer={setMerchantDamageObjectParcer}
                        partActionData={card}
                        isInteractionSubmiting={isFormSubmitting}
                        submitInteractionForms={submitInteractionForms}
                      />
                    )}
                  </PartActionCardWrapper>
                ))}
              </PartActionContainer>
              <Grid container mt={2} spacing={2} justifyContent="space-between" mx={isMobileView ? -1 : 2}>
                <Grid size={{ xs: 4, sm: 4, md: 3.5, xl: 3.5 }}>
                  <Button
                    fullWidth
                    variant={ButtonVariantProps.Secondary}
                    color={ButtonColorProps.Info}
                    onClick={() => setIsPartActionPopup(true)}
                    disabled={partActionsExecuting || (interceptedFormsObject && interceptedFormsObject?.forms?.length > 0)}
                  >
                    + Action
                  </Button>
                </Grid>
                <Grid size={{ xs: 4, sm: 4, md: 3.5, xl: 3.5 }}>
                  <Button
                    fullWidth
                    variant={ButtonVariantProps.Primary}
                    color={ButtonColorProps.Success}
                    startIcon={partActionsExecuting ? <Loader size={20} /> : null}
                    onClick={onPartActionsBulkSubmit}
                    disabled={
                      partActionsExecuting ||
                      (interceptedFormsObject && interceptedFormsObject?.forms?.length > 0) ||
                      isPartActionsSubmitDisabled(partActionCardDataset)
                    }
                  >
                    Submit
                  </Button>
                </Grid>
                <Grid size={{ xs: 4, sm: 4, md: 3.5, xl: 3.5 }}>
                  <Button
                    fullWidth
                    variant={ButtonVariantProps.Secondary}
                    color={ButtonColorProps.Error}
                    onClick={() => onPartActionsCancel()}
                    disabled={partActionsExecuting}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </>
          ) : (
            isPendingInteractions ?
              <Grid container spacing={GRID_SPACING} direction="column">
                {pendingInteractionFormData?.map((form, index) => (
                  <Grid
                    key={index}
                    size={{
                      sm: 12,
                      md: 6,
                      xl: 4
                    }}>
                    <FormInterceptor
                      key={index}
                      service={service}
                      value={form}
                      onChange={(editedForms) => {
                        if (repeatableFormData.templateId === 0 && repeatableFormData.templateTitle === '') {
                          const updatedForms = pendingInteractionFormData.map((form) => form.id === editedForms.id ? editedForms : form);
                          setPendingInteractionFormData(updatedForms);
                          setInterceptedFormsObject({
                            url: `/pendingInteractions/${service.id}`,
                            method: HttpMethod.Put,
                            payload: '',
                            forms: updatedForms
                          });
                        }
                      }}
                      onDamagedPartSelect={(Objectkey: string, objectValue: FormData | number | MerchantDamagePartSerialMenuData) => {
                        setMerchantDamageObjectParcer(prevState => ({ ...prevState, [Objectkey]: objectValue }));
                      }}
                      onAddForm={(templateId, templateTitle) => setRepeatableFormData({ templateId, templateTitle })}
                      isRepeatable={getFormIsRepeatableStatus(form, pendingInteractionFormData)}
                      forms={pendingInteractionFormData || []}
                    />
                  </Grid>
                ))}
                <div>
                  <br />
                  {isFormSubmitting ?
                    <Loader />
                    :
                    <Stack direction="row" spacing={2} alignItems="center" ml={3}>
                      <Button
                        variant={ButtonVariantProps.Primary}
                        color={ButtonColorProps.Success}
                        onClick={handleOnSubmitInteractionForms}
                        disabled={blockInteractionAvailable(permissions || [], pendingInteractionFormData || [])}
                      >
                        Submit
                      </Button>
                      {blockInteractionAvailable(permissions || [], pendingInteractionFormData || []) && <Typography textColor={COLORS.Red}>{getBlockInteractionMessage(pendingInteractionFormData || [])}</Typography>}
                    </Stack>
                  }
                </div>
              </Grid>
              :
              <Grid container spacing={1} alignItems="center">
                <Grid
                  size={{
                    xs: 12,
                    sm: 12,
                    md: 3
                  }}>
                  <Stack direction="row">
                    {isStatusChange &&
                      <SelectStatusContainer direction="row">
                        <ForwardIcon fontSize="large" color="action" />
                        <Stack width="100%">
                          <SelectMenu
                            id="service-status-menu"
                            label="Select Status"
                            items={statusOptionItems(isCancelOrReopen && getServiceCancelOrReopenStatuses.data ? getServiceCancelOrReopenStatuses.data : statusOptions, !service.serviceStatus.isClosed, isCancelOrReopen, currentStatusLabel)}
                            selectedValue={statusValue}
                            onChange={(val) => {
                              onChangeStatusValue(val);
                            }}
                            disabled={!isUserHasPermission(ActionPermissions.Service_Edit_Status_Progress_Status_Change, permissions)}
                            validate={validate}
                            optionalLabelEnabled={true}
                            optionalLabel="Select Status"
                          />
                        </Stack>
                      </SelectStatusContainer>
                    }
                  </Stack>
                </Grid>
                {isStatusChange &&
                  <>
                    { <Grid
                      mb={1.5}
                      size={{
                        xs: 12,
                        md:12,
                        lg: 9
                      }}
                      sx={{
                        '@media (max-width:1099px)': {
                          width: '100% !important',
                        },
                        '@media (min-width:1100px)': {
                          width: '70% !important',
                        }
                      }}
                    >
                      <Grid container spacing={1} alignItems="flex-start" mb={1}>
                        <Grid
                          size={{
                            xs: 12,
                            sm: 12,
                            md: 5,
                            lg: 6
                          }}
                          sx={{
                            '@media (max-width:1099px)': {
                              width: '100% !important',
                            },
                          }}
                        >
                          <Stack direction="row" ml={0}
                            sx={{
                              marginLeft: '0px'
                            }}
                          >
                            {!interceptedFormsObject?.isCheckin &&
                              <NotesText
                                label={isCancelOrReopen === ServiceCancelOrReOpen.ReOpen.toString() && noteText === '' ? 'Provide a note to re-open the service' : 'Notes'}
                                multiline
                                rows={1}
                                value={noteText}
                                onChange={(text) => onNoteText(text)}
                              />
                            }
                            {!interceptedFormsObject?.forms?.length &&
                            <StatusSaveButtonWrap>
                              <Button variant={ButtonVariantProps.Primary} color={ButtonColorProps.Success} onClick={handleSubmit} size={ButtonSizeProps.Small}>
                                Save
                              </Button>
                            </StatusSaveButtonWrap>
                            }
                          </Stack>
                        </Grid>
                      </Grid>
                    </Grid>}
                  </>
                }
                {isStatusChange && interceptedFormsObject?.isCheckin &&
                  <ServiceNewCheckIn 
                    travelStart={travelStart}
                    onSiteTime={onSiteTime}
                    noteText={notesText}
                    onNoteText={(val) => setNotesText(val)}
                    onChangeTravelStart={(val) => setTravelStart(val)}
                    onChangeOnSiteTime={(val) => setOnSiteTime(val)}
                    onChangeAllocation={(val, checked) => {
                      if(!val?.isUserCheckedIn) {
                        const updatedCheckins: AllocatedService[] = [ ...checkedInService ];
                        if (checked) {
                          updatedCheckins.push(val);
                        } else {
                          const removeIndex = updatedCheckins.findIndex((checkin) => checkin.serviceId === val.serviceId);
                          updatedCheckins.splice(removeIndex, 1);
                        }
                        setCheckedInService(updatedCheckins);
                      }
                      if(val?.isUserCheckedIn) {
                        const updatedNotCheckins: AllocatedService[] = [ ...notCheckedInService ];
                        if (!checked) {
                          updatedNotCheckins.push(val);
                        } else {
                          const removeIndex = updatedNotCheckins.findIndex((notCheckin) => notCheckin.serviceId === val.serviceId);
                          updatedNotCheckins.splice(removeIndex, 1);
                        }
                        setNotCheckedInService(updatedNotCheckins);
                      }
                    }}
                    allocatedServiceData={allocatedServices}
                    allocatedServicesLoading={getCurrentUserAllocatedServices.isLoading}
                    multiOnSiteSuggestionsAvailable={interceptedFormsObject?.multiOnSiteSuggestionsAvailable}
                  />
                }
                {isAllocationChange &&
                  <ServiceAllocationSelector
                    allocation={selectedNodeAllocation}
                    defaultAllocationSuggetions={suggestedNodesFromAllocation}
                    onSubmit={(val) => {
                      onAllocationChange(val);
                      setIsAllocationChange(false);
                    }}
                    onClose={() => setIsAllocationChange(false)}
                    permissions={permissions}
                  />
                }
                {isStatusChange || isAllocationChange ||
                  <>
                    {isHelpdeskUser && currentStatusLabel === ServiceStatusLabel.OnSite ?
                      <>
                        {technicians.slice(0, 2).map((technician: Technician) =>
                          <Stack direction="row" alignItems="center" spacing={1} key={technician.id}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <IconContainer backgroundColor="#1769AA1F"><AccountIcon /></IconContainer>
                              <AccountText>{technician.name}</AccountText>
                            </Stack>
                          </Stack>
                        )}
                        <Grid container spacing={1} justifyContent="space-between">
                          <Grid
                            size={{
                              xs: 12,
                              md: 4
                            }}>
                            <MenuButton
                              aria-describedby="service-status-menu"
                              variant="text"
                              color="inherit"
                              onClick={(event) => setAnchorElTechnicians(event.currentTarget)}
                            >
                              <IconContainer backgroundColor="#1769AA1F"><AccountIcon /></IconContainer>
                            </MenuButton>
                          </Grid>
                          <Grid
                            size={{
                              xs: 12,
                              md: 4
                            }}>
                            <Menu
                              id="technician-list-menu"
                              anchorEl={anchorElTechnicians}
                              open={openTechniciansMenu}
                              onClose={() => setAnchorElTechnicians(null)}
                              MenuListProps={{ 'aria-labelledby': 'technician-list-menu' }}
                            >
                              {technicians.map((technician: Technician) =>
                                <MenuItem key={technician.id}>{technician.name}</MenuItem>
                              )}
                            </Menu>
                          </Grid>
                          <Grid
                            size={{
                              xs: 12,
                              md: 4
                            }}>
                            <MenuButton
                              aria-describedby="service-status-menu"
                              variant="text"
                              color="inherit"
                              onClick={(event) => setAnchorEl(event.currentTarget)}
                            >
                              <MenuIcon />
                            </MenuButton>
                          </Grid>
                          <Grid
                            size={{
                              xs: 12,
                              md: 4
                            }}>
                            <Badge 
                              color="secondary" 
                              variant="dot" 
                              invisible={!partActionCardDataset.length || !isUserHasPartActionPermissions(permissions, interceptedFormsObject?.isCheckin || false, service.serviceStatusCode)} 
                              style={{ transform: 'translate(6px, 6px)' }}
                            >
                              <Menu
                                id="service-status-menu"
                                anchorEl={anchorEl}
                                open={open}
                                onClose={() => setAnchorEl(null)}
                                MenuListProps={{ 'aria-labelledby': 'service-status-menu' }}
                              >
                                {isUserHasPartActionPermissions(permissions, interceptedFormsObject?.isCheckin || false, service.serviceStatusCode) &&
                                  <FullWidthBadge color="secondary" invisible={!partActionCardDataset.length} badgeContent={partActionCardDataset.length} overlap="circular" className="css">
                                    <MenuItem onClick={onPartActionMenuItemClick}>Part Actions</MenuItem>
                                  </FullWidthBadge>
                                }
                                {isUserHasPermission(ActionPermissions.Service_Edit_Actions_Tasks, permissions) && <MenuItem onClick={() => setIsTask(!isTask)}>Tasks</MenuItem>}
                              </Menu>
                            </Badge>
                          </Grid>
                        </Grid>
                      </>
                      :
                      <Stack direction="row" width="100%" marginBottom={0.75} marginTop={0.25}>
                        <Grid container display="flex" justifyContent="flex-start" width="100%" spacing={1} columnSpacing={4}>
                          {!isStatusChange && !isAllocationChange &&
                            <StatusbarItemGrid
                              justifyContent={{ md: 'flex-start' }}
                              size={{
                                xs: 12,
                                sm: 6,
                                md: 4
                              }}
                              onClick={() => !service.serviceStatus.isClosed && setIsStatusChange(!isStatusChange)}
                            >
                              <StatusbarItemHeadingGrid><StatusbarHeadingText variant={TypographyVariantProps.Subtitle2}>Status:</StatusbarHeadingText></StatusbarItemHeadingGrid>
                              <NewButton
                                isMobile={true}
                                startIcon={<LightbulbIcon />}
                                variant={ButtonVariantProps.Secondary}
                              >
                                {currentStatusLabel}
                              </NewButton>
                            </StatusbarItemGrid>
                          }
                          {isUserHasPermission(ActionPermissions.Service_Edit_Allocation_Primary, permissions) && 
                            <StatusbarItemGrid
                              justifyContent={{ md: 'flex-start' }}
                              size={{
                                xs: 12,
                                sm: 6,
                                md: 4
                              }}
                              onClick={() => {
                                setIsAllocationChange(true);
                                setSelectedNodeAllocation({
                                  ...primaryAllocation,
                                  allocationType: ServiceNodeAllocationType.Primary
                                });
                              }}
                            >
                              <StatusbarItemHeadingGrid><StatusbarHeadingText variant={TypographyVariantProps.Subtitle2}>1st Alloc:</StatusbarHeadingText></StatusbarItemHeadingGrid>
                              {primaryAllocation && primaryAllocation.node ?
                                <ClickableStack direction="row" alignItems="flex-end" spacing={1}>
                                  <StyledActionBarStack isMobile={true}>
                                    <IconContainer backgroundColor="transparent"><PrimaryAllocationIcon sx={{ fontSize: 20 }} /></IconContainer>
                                    {primaryAllocation.parentNode && primaryAllocation.parentNode.id > 0 ?
                                      <Stack direction="column">
                                        <LocationText isMobile={true}>{primaryAllocation.parentNode.name}</LocationText>
                                        <Stack direction="row">
                                          <SubdirectoryArrowIcon />
                                          <LocationSubText isMobile={true}>{primaryAllocation.node.name}</LocationSubText>
                                        </Stack>
                                      </Stack>
                                      :
                                      <Stack width="100%" alignItems="center">
                                        <LocationText isMobile={true}>{primaryAllocation.node.name}</LocationText>
                                      </Stack>
                                    }
                                  </StyledActionBarStack>
                                </ClickableStack>
                                :
                                <NewButton
                                  isMobile={true}
                                  startIcon={<PrimaryAllocationIcon />}
                                  variant={ButtonVariantProps.Secondary}
                                >
                                  Allocate
                                </NewButton>
                              } 
                            </StatusbarItemGrid> 
                          }
                          {isUserHasPermission(ActionPermissions.Service_Edit_Allocation_Secondary, permissions) && 
                            <StatusbarItemGrid
                              justifyContent={{ md: 'flex-start' }}
                              size={{
                                xs: 12,
                                sm: 6,
                                md: 4
                              }}
                              onClick={() => {
                                setIsAllocationChange(true);
                                setSelectedNodeAllocation({
                                  ...secondaryAllocation,
                                  allocationType: ServiceNodeAllocationType.Secondary
                                });
                              }}
                            > 
                              <StatusbarItemHeadingGrid><StatusbarHeadingText variant={TypographyVariantProps.Subtitle2}>2nd Alloc:</StatusbarHeadingText></StatusbarItemHeadingGrid>
                              {secondaryAllocation && secondaryAllocation.node ?
                                <ClickableStack direction="row" alignItems="flex-end" spacing={1}>
                                  <StyledActionBarStack isMobile={true}>
                                    <IconContainer backgroundColor="transparent"><SecondaryAllocationIcon sx={{ fontSize: 20 }} /></IconContainer>
                                    {secondaryAllocation.parentNode && secondaryAllocation.parentNode.id > 0 ?
                                      <Stack direction="column">
                                        <LocationText isMobile={true}>{secondaryAllocation.parentNode.name}</LocationText>
                                        <Stack direction="row">
                                          <SubdirectoryArrowIcon />
                                          <LocationSubText isMobile={true}>{secondaryAllocation.node.name}</LocationSubText>
                                        </Stack>
                                      </Stack>
                                      :
                                      <Stack width="100%" alignItems="center">
                                        <LocationText isMobile={true}>{secondaryAllocation.node.name}</LocationText>
                                      </Stack>
                                    }
                                  </StyledActionBarStack>
                                </ClickableStack>
                                :
                                <NewButton
                                  isMobile={true}
                                  startIcon={<SecondaryAllocationIcon />}
                                  variant={ButtonVariantProps.Secondary}
                                >
                                  Allocate
                                </NewButton>
                              } 
                            </StatusbarItemGrid> 
                          }
                        </Grid>
                        <Stack direction="row" alignItems="center" justifyContent="center" paddingLeft={isMobileView ? -1 : 4}>
                          <MenuButton
                            aria-describedby="service-status-menu"
                            variant="text"
                            color="inherit"
                            onClick={(event) => setAnchorEl(event.currentTarget)}
                          >
                            <ControlPointRoundedIcon />
                          </MenuButton>
                          <Badge 
                            color="secondary" 
                            variant="dot" 
                            invisible={(!partActionCardDataset.length && !taskCardDataset.length && !getPendingInteractionsByServiceId.data?.length) || !isUserHasPartActionPermissions(permissions, interceptedFormsObject?.isCheckin || false, service.serviceStatusCode)} 
                            style={{ transform: 'translate(6px, 6px)' }}
                          >
                            <Menu
                              id="service-status-menu"
                              anchorEl={anchorEl}
                              open={open}
                              onClose={() => setAnchorEl(null)}
                              MenuListProps={{ 'aria-labelledby': 'service-status-menu' }}
                              sx={{ width: '50%' }}
                            >
                              {isUserHasPartActionPermissions(permissions, interceptedFormsObject?.isCheckin || false, service.serviceStatusCode) &&
                                <FullWidthBadge color="secondary" invisible={!partActionCardDataset.length} badgeContent={partActionCardDataset.length} overlap="circular" className="css">
                                  <FullWidthMenuItem onClick={onPartActionMenuItemClick}><HandymanIcon sx={{ marginRight: '8px' }}/>Part Actions</FullWidthMenuItem>
                                </FullWidthBadge>
                              }
                              {isUserHasPermission(ActionPermissions.Service_Edit_Actions_Tasks, permissions) &&
                                <FullWidthBadge color="secondary" invisible={!taskCardDataset.length} badgeContent={taskCardDataset.length} overlap="circular" className="css">
                                  <FullWidthMenuItem onClick={() => setIsTask(!isTask)}><ViewInArIcon sx={{ marginRight: '8px' }}/>Tasks</FullWidthMenuItem>
                                </FullWidthBadge>
                              }
                              <FullWidthBadgeInteraction color="secondary" invisible={!getPendingInteractionsByServiceId.data?.length} badgeContent={getPendingInteractionsByServiceId.data?.length} overlap="circular" className="css">
                                <FullWidthMenuItem disabled={!getPendingInteractionsByServiceId.data?.length} onClick={clickPendingInteractions}><RocketLaunchIcon sx={{ marginRight: '8px' }}/>Pending Interactions</FullWidthMenuItem>
                              </FullWidthBadgeInteraction>
                              {checkServiceCancelOrFutileAvailability(permissions || [], interceptedFormsObject?.isCheckin || false, service.serviceStatusCode) && !service.serviceStatus.isClosed &&
                                <MenuItem onClick={() => setIsCancelOrReopen(ServiceCancelOrReOpen.CancelFutile)}><DisabledByDefaultOutlinedIcon sx={{ marginRight: '8px' }}/>Cancel/Futile</MenuItem>
                              }
                              {isUserHasPermission(ActionPermissions.Service_Edit_Status_Progress_Reopen_Service, permissions) && service.serviceStatus.isClosed &&
                                <MenuItem onClick={() => setIsCancelOrReopen(ServiceCancelOrReOpen.ReOpen)}><ZapSquareIcon />Re-Open</MenuItem>
                              }
                            </Menu>
                          </Badge>
                        </Stack>
                      </Stack>
                    }
                  </>
                }
              </Grid>
          )}
        </Stack>
      </ActionBarStack>

      {!pendingInteractionFormData && !isPartAction &&
        interceptedFormsObject?.forms?.map((form, index) =>
          <FormInterceptor
            key={index}
            service={service}
            value={form}
            onChange={(editedForms) => {
              if (repeatableFormData.templateId === 0 && repeatableFormData.templateTitle === '') {
                const updatedForms = interceptedFormsObject?.forms.map((formData) => formData.id === editedForms.id ? editedForms : formData);
                setInterceptedFormsObject({
                  ...interceptedFormsObject,
                  forms: updatedForms
                });
              }
            }}
            onDamagedPartSelect={(Objectkey: string, objectValue: FormData | number | MerchantDamagePartSerialMenuData) => {
              setMerchantDamageObjectParcer(prevState => ({ ...prevState, [Objectkey]: objectValue }));
            }}
            onAddForm={(templateId, templateTitle) => setRepeatableFormData({ templateId, templateTitle })}
            isRepeatable={getFormIsRepeatableStatus(form, interceptedFormsObject.forms)}
            forms={interceptedFormsObject.forms}
          />
        )}
      {!pendingInteractionFormData && !isPartAction && interceptedFormsObject?.forms && interceptedFormsObject?.forms?.length > 0 &&
        <div>
          <br/>
          {isFormSubmitting ?
            <Loader />
            :
            <Stack direction="row" spacing={2} alignItems="center" ml={2}>
              <Button
                variant={ButtonVariantProps.Primary}
                color={ButtonColorProps.Success}
                onClick={handleOnSubmitInteractionForms}
                disabled={(isCancelOrReopen === ServiceCancelOrReOpen.ReOpen && noteText === '') || blockInteractionAvailable(permissions || [], interceptedFormsObject?.forms)}
              >
                Submit
              </Button>
              {blockInteractionAvailable(permissions || [], interceptedFormsObject?.forms) && <Typography textColor={COLORS.Red}>{getBlockInteractionMessage(interceptedFormsObject?.forms)}</Typography>}
            </Stack>
          }
        </div>
      }
      <PopupDialog
        width="50%"
        maxWidth="300px"
        title="Pending Shipment"
        isOpen={openShipmentReceivePopup}
        actionLabel="Accept"
        cancelButtonLable="Cancel"
        onClose={() => {
          const partActionCard = partActionCardDataset.find(partAction => partAction.value.id === selectedPartActionCard?.value.id);
          if(partActionCard) {            
            pendingShipmentsDataRef.current = {
              accepted: pendingShipmentsDataRef.current.accepted,
              canceled: [
                ...pendingShipmentsDataRef.current.canceled,
                {
                  ...partActionCard,
                  value: { ...partActionCard.value, partId: NaN, serial: '' }
                }
              ]
            };
          }
          setOpenShipmentReceivePopup(false);
          pendingShipmentDialogData?.onCancel(); 
        }}
        onSave={async() => {
          await receiveShipment(shipmentManifestSummery?.partManifestId, shipmentManifestSummery);
          pendingShipmentsDataRef.current = {
            accepted: [
              ...pendingShipmentsDataRef.current.accepted,
              selectedPartActionCard as CustomisePartActionCard
            ],
            canceled: pendingShipmentsDataRef.current.canceled
          };
          setOpenShipmentReceivePopup(false);
          pendingShipmentDialogData?.onOk();
        }}
      >
        <PartActionShipmentReceive
          shipmentManifestSummery={shipmentManifestSummery}
          currentNumber={pendingShipmentDialogData?.currentNumber || 1}
          total={pendingShipmentDialogData?.total || 1}
        />
      </PopupDialog>
      <PopupDialog
        width="90%"
        maxWidth="500px"
        title="Create Serialized Part"
        isOpen={openPartCreationPopup}
        actionLabel="Submit"
        cancelButtonLable="Cancel"
        onClose={() => setOpenPartCreationPopup(false)}
        onSave={() => onPartSave()}
      >
        <PartActionPartCreationDialog
          isOpen={openPartCreationPopup}
          helperText={helperText?.find((text) => text.id === pendingPartCardId) || SAMPLE_PART_ACTION_CARD_HELPER_TEXT}
          partActionData={selectedPartActionData}
          partTypeImages={partTypeImages}
          onPartImagesUpdate={setPartImages}
          setPartTypeSearchValue={setPartTypeSearchValue}
          partTypeValue={partTypeValue}
          partActionType={partActionCardType}
          service={service}
          onSerialChange={(value) => setSelectedPartActionData({ ...selectedPartActionData as PartCreationOptionData, newPartSerial: value })}
        ></PartActionPartCreationDialog>
      </PopupDialog>
      <PopupDialog
        width="85%"
        maxWidth="400px"
        title="Part Actions"
        isOpen={isPartActionPopup}
        onClose={(event?: any, reason?: string) => (reason !== 'backdropClick') && setIsPartActionPopup(false)}
        onSave={onPopupSave}
      >
        <StyledSelectMenu
          id="part-actions-menu"
          label="Select Part Actions"
          minWidth="100%"
          items={getPartActionTypesDropdownData(getPartActionTypes.data || [])}
          selectedValue={partActionCardType}
          onChange={(type: string) => setPartActionCardType(type as PartActionType)}
        />
      </PopupDialog>
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'right' }} open={showWarningToast} autoHideDuration={8000} onClose={() => setShowWarningToast(false)}>
        <Alert onClose={() => setShowWarningToast(false)} severity="warning">
          <AlertTitle>Warning</AlertTitle>
          Service creation form has errors and continuing will not create a service. Please fill the required fields.
        </Alert>
      </Snackbar>
    </ContentPaper>
  );
};